import { useState, useEffect, useRef } from "react";
import { PLATFORM_TIMEZONE } from "../../../utils/timezone";
import { Check, Link2, Trash2, Upload } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../../styles/__lab_appointment_detail.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import UserAvatar from "../../../assets/images/patientAvatar.png";
import reportPreview from "../../../assets/images/reportPreview.png";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LiaMapMarkerSolid } from "react-icons/lia";
import Chat from "../../../components/chat/ChatModal";
import useRejectionReasons, {
  CUSTOM_REJECTION_REASON,
} from "../../../hooks/useRejectionReasons";
import TestSearchSelect from "../../../components/test-search-select/TestSearchSelect";
import AmountInput from "../../../components/amount-input/AmountInput";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import {
  composeBookingChildName,
  getBookingChildDob,
  getBookingChildGender,
} from "../../../utils/childBooking";
import { IoIosCheckbox } from "react-icons/io";
import { AiOutlineFilePdf } from "react-icons/ai";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import ImagingInvoiceModal from "../../../components/imaging-invoice-modal/ImagingInvoiceModal";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchImagingOrderDetails,
  clearOrderDetails,
  fetchImagingServices,
} from "../../../slice/imagingOrders/imagingOrdersSlice";
import Loader from "../../../components/loader/Loader";
import {
  FaArrowLeftLong,
  FaLocationArrow,
  FaCirclePlus,
} from "react-icons/fa6";
import { RxChevronDown, RxClock } from "react-icons/rx";
import { FaClock, FaFileInvoice, FaInfoCircle } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { FiPrinter } from "react-icons/fi";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "../../../components/toast";
import { APP_PATH } from "../../../api/backendPath";
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric, formatCustomInLocale as tzLocale, nowInDisplayZone } from "../../../utils/datetime";
import { formatPercent } from "../../../utils/amountFormat";

// Result link: the second way to deliver results (paste a link instead of uploading a
// PDF). Same rule as IMAGING_RESULT_LINK_LIMITS in tiryaq-healthcare-shared, which the
// api-gateway and lab-imaging-service enforce: an absolute http(s) URL of at most 2048
// characters. Other schemes are refused because the link is rendered as an href.
const RESULT_LINK_MAX_LENGTH = 2048;
const isValidResultLink = (value) => {
  if (typeof value !== "string") return false;
  const link = value.trim();
  if (!link || link.length > RESULT_LINK_MAX_LENGTH) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F\s]/.test(link)) return false;
  try {
    const url = new URL(link);
    return (
      (url.protocol === "http:" || url.protocol === "https:") && !!url.hostname
    );
  } catch {
    return false;
  }
};

// Canonical location codes for the imaging order flow. Kept as constants so the
// values aren't hardcoded as magic strings throughout the component.
const LOCATION_CODES = {
  HOME_VISIT: "HOME_VISIT",
  ON_SITE: "ON_SITE",
};

// The preferred/service location can arrive in several shapes from the API
// (e.g. "Home Visit" / "HOME_VISIT" or "Visit Center" / "On-Site" / "ON_SITE" /
// "VISIT_CENTER"). Normalize any of them to a single canonical location code.
const normalizeLocationCode = (value) => {
  if (!value) return LOCATION_CODES.ON_SITE;
  const normalized = String(value)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "HOME_VISIT" || normalized === "HOMEVISIT"
    ? LOCATION_CODES.HOME_VISIT
    : LOCATION_CODES.ON_SITE;
};

// The provider's consultation type (configured by admin) constrains which test
// locations can be chosen: Online (video) → On-Site only, Home Visit (onsite) →
// Home Visit only, Both (video-onsite) → both. Unknown/empty → both (backward
// compatible so existing orders are never blocked).
const allowedLocationsFromConsultation = (type) => {
  const key = type ? String(type).trim().toLowerCase() : "";
  if (key === "video" || key === "online") return [LOCATION_CODES.ON_SITE];
  if (key === "onsite" || key === "home_visit" || key === "homevisit")
    return [LOCATION_CODES.HOME_VISIT];
  return [LOCATION_CODES.ON_SITE, LOCATION_CODES.HOME_VISIT];
};

// Derive whole-years age from a 'YYYY-MM-DD' (or ISO) date-of-birth string.
// Returns an integer year count, or null when the input is missing/invalid or
// in the future — so callers can safely fall back to a placeholder.
const getWholeYearsAge = (dob) => {
  if (!dob) return null;
  const parts = String(dob).split("-");
  const birth =
    parts.length >= 3
      ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      : new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate()))
    years -= 1;
  return years < 0 ? null : years;
};

// Capitalize the first letter of a word (e.g. "male" → "Male"). Null-safe.
const capitalizeWord = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const GreenCheckSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z"
      fill="#10B981"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
      fill="#10B981"
    />
  </svg>
);

const RedXSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z"
      fill="#EF4444"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z"
      fill="#EF4444"
    />
  </svg>
);

const ImagingAppointmentDetail = ({ onLogout }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Order Details");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [serviceTime, setServiceTime] = useState("");
  const [serviceDate, setServiceDate] = useState(null);

  // ===== Imaging center operating-hours slots =====
  // Slots for the selected service date come from
  // GET /api/v1/imaging/center-slots (center resolved from the JWT).
  // Service-date-based slots — drives "Center Availability" + the Service Time
  // options (recomputed whenever the officer changes the service date).
  const [centerSlots, setCenterSlots] = useState(null);
  // Preferred-date-based slots — drives ONLY the Preferred Time Slot badge/range,
  // pinned to the patient's original preferred date (does NOT change with the
  // selected service date).
  const [preferredSlots, setPreferredSlots] = useState(null);
  // Signature of the availability banner the user dismissed, so it stays hidden
  // for that exact situation but re-appears when the reason/date changes.
  const [dismissedBannerKey, setDismissedBannerKey] = useState(null);
  // Whether the "no time slots for this date" tooltip is dismissed (reset when
  // the officer picks a different service date).
  const [serviceTimeTipDismissed, setServiceTimeTipDismissed] = useState(false);
  // True only when the officer actively picks a service date, so the "closed"
  // toast fires on selection but not on the initial page load.
  const dateSelectedByUserRef = useRef(false);

  // Provider's consultation type (configured by admin) — constrains the test
  // location options. Null until fetched → both locations allowed.
  const [consultationType, setConsultationType] = useState(null);
  const allowedLocations = allowedLocationsFromConsultation(consultationType);

  const {
    orderDetails: order,
    isDetailsLoading: loading,
    imagingServices,
    isServicesLoading,
  } = useSelector((state) => state.imagingOrders);
  // The child's composed name on a child booking, empty otherwise. Drives the header split (child
  // as the patient, account holder as the guardian); empty also when the snapshot carries no
  // name, so the header then keeps the account holder instead of going blank.
  const childHeaderName = composeBookingChildName(order);

  // Fetch the provider's consultation type once (constrains test locations).
  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const axiosInstance = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: { Authorization: `Bearer ${token}` },
        });
        const res = await axiosInstance.get("/api/v1/imaging/profile");
        const type = res?.data?.data?.consultation_type;
        if (type) setConsultationType(type);
      } catch (e) {
        // Best-effort — on failure both locations remain allowed.
      }
    })();
  }, []);

  useEffect(() => {
    if (id) dispatch(fetchImagingOrderDetails(id));
    dispatch(fetchImagingServices());
    fetchImagingFees();
    fetchImagingCenterProfile();
    return () => {
      dispatch(clearOrderDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Silent refresh when a real-time notification arrives
  useEffect(() => {
    const handleNotification = () => {
      if (id) dispatch(fetchImagingOrderDetails(id));
    };
    window.addEventListener(
      "imaging_notification_received",
      handleNotification,
    );
    return () =>
      window.removeEventListener(
        "imaging_notification_received",
        handleNotification,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Parse a YYYY-MM-DD (or ISO) date string as a LOCAL calendar date so the day never
  // shifts with the browser's timezone. Null when it can't be read.
  const parseYMDLocal = (value) => {
    if (!value || value === "null") return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    if (order?.scheduledTime) setServiceTime(order.scheduledTime);
    // Parsed as a local calendar day so the saved date shows correctly in the input.
    const savedDate = parseYMDLocal(order?.scheduledDate);
    if (savedDate) setServiceDate(savedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.scheduledTime, order?.scheduledDate]);

  // Switch to "Order Details" tab if current tab is not available for the order status
  useEffect(() => {
    if (
      order?.status === "REQUEST_SUBMITTED" &&
      activeTab === "Results & Reports"
    ) {
      setActiveTab("Order Details");
    }
  }, [order?.status, activeTab]);

  // ── Time slot helpers ──────────────────────────────────────────────────────
  // Period bands (minutes since midnight) matching the backend slot buckets.
  const PERIOD_BANDS = {
    MORNING: { start: 0, end: 12 * 60 },
    AFTERNOON: { start: 12 * 60, end: 17 * 60 },
    EVENING: { start: 17 * 60, end: 24 * 60 },
  };

  const hhmmToMinutes = (value) => {
    if (!value) return NaN;
    const [h, m] = String(value).split(":").map(Number);
    if (isNaN(h)) return NaN;
    return h * 60 + (isNaN(m) ? 0 : m);
  };

  const minutesToHHMM = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

  const minutesToLabel12 = (mins) => {
    const norm = ((mins % 1440) + 1440) % 1440;
    const h24 = Math.floor(norm / 60);
    const m = norm % 60;
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  const formatDateYMD = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const mo = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  };

  // The service can be on the patient's preferred date or any later day. The calendar
  // starts at the preferred date, or at today (Baghdad) once the preferred date has passed.
  const preferredServiceDate = parseYMDLocal(order?.preferredDate);
  const todayServiceDate = parseYMDLocal(nowInDisplayZone().date);
  const minServiceDate =
    preferredServiceDate && preferredServiceDate > todayServiceDate
      ? preferredServiceDate
      : todayServiceDate;
  // Toasts when the picked service date is before the preferred date. The date already
  // saved on the order is accepted as-is (the backend applies the same rule).
  const blockServiceDateBeforePreferred = () => {
    if (!serviceDate || !preferredServiceDate) return false;
    const picked = formatDateYMD(serviceDate);
    if (picked >= formatDateYMD(preferredServiceDate)) return false;
    if (picked === String(order?.scheduledDate || "").slice(0, 10)) return false;
    toast.error(
      `Service date cannot be before the patient's preferred date (${formatDateYMD(preferredServiceDate).split("-").reverse().join("/")}).`,
    );
    return true;
  };

  // Preferred-slot window for the patient's preferred period, clipped to the
  // center's operating hours ON THE PREFERRED DATE (pinned — not affected by the
  // selected service date). Ends before the closing time (band end),
  // e.g. Morning + 8AM–1PM hours => 8:00 AM – 12:00 PM.
  // Window for the patient's preferred period clipped to a given day's operating
  // hours (from a center-slots payload). Ends before the closing time (band end),
  // e.g. Morning + 8AM–1PM hours => 8:00 AM – 12:00 PM. Null when the day is
  // closed or the period doesn't overlap the operating hours.
  const getSlotWindowFrom = (slotsData) => {
    const slotKey = (order?.preferredTimeSlot || "").toUpperCase();
    const band = PERIOD_BANDS[slotKey];
    if (!band) return null;
    if (!slotsData || slotsData.is_open === false) return null;
    const oh = slotsData.operating_hours;
    if (!oh) return null;
    const start = Math.max(band.start, hhmmToMinutes(oh.start));
    const end = Math.min(band.end, hhmmToMinutes(oh.end));
    if (!(start < end)) return null;
    return { startMin: start, endMin: end };
  };

  // Preferred-slot window pinned to the PREFERRED date (not affected by the
  // selected service date).
  const getPreferredSlotWindow = () => getSlotWindowFrom(preferredSlots);

  // Format a YYYY-MM-DD (or ISO) string as dd/MM/yyyy.
  const formatYMDLabel = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split("-");
    const d =
      parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(ymd);
    return isNaN(d.getTime()) ? null : tzLocale(d, "en-GB", undefined);
  };

  const preferredPeriodName = (() => {
    const slotKey = (order?.preferredTimeSlot || "").toUpperCase();
    return slotKey === "MORNING"
      ? "Morning"
      : slotKey === "AFTERNOON"
        ? "Afternoon"
        : slotKey === "EVENING"
          ? "Evening"
          : "preferred";
  })();

  const hoursTextFrom = (oh) =>
    oh && oh.start && oh.end
      ? `${minutesToLabel12(hhmmToMinutes(oh.start))} - ${minutesToLabel12(hhmmToMinutes(oh.end))}`
      : null;

  // Single source of truth for the preferred-slot availability. Returns whether
  // the center is open for the patient's preferred period on the preferred date,
  // plus the reason it's closed:
  //  - CLOSED_DAY  : the center is closed on the preferred date.
  //  - OUT_OF_HOURS: the preferred period is outside that day's operating hours.
  //  - PAST        : the preferred date is today and the operating hours ended.
  const evaluatePreferredSlotStatus = () => {
    if (!preferredSlots) return { loaded: false, open: false, reason: null };
    if (preferredSlots.is_open !== true)
      return { loaded: true, open: false, reason: "CLOSED_DAY" };
    const win = getPreferredSlotWindow();
    if (!win) return { loaded: true, open: false, reason: "OUT_OF_HOURS" };
    const todayStr = nowInDisplayZone().date; // Baghdad date
    if (preferredSlots.date === todayStr) {
      const closeMin = hhmmToMinutes(preferredSlots?.operating_hours?.end);
      const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
      if (!isNaN(closeMin) && nowMinutes >= closeMin)
        return { loaded: true, open: false, reason: "PAST" };
    }
    return { loaded: true, open: true, reason: null };
  };

  // Human-readable banner content (title + detail) explaining why the preferred
  // slot is closed, referencing the patient's preferred slot, the date, and the
  // center's operating hours so the officer understands it's out of time.
  const getPreferredSlotClosedMessage = (reason) => {
    const dateLabel = formatYMDLabel(order?.preferredDate);
    const onDate = dateLabel ? ` on ${dateLabel}` : "";
    const hoursText = hoursTextFrom(preferredSlots?.operating_hours);
    if (reason === "CLOSED_DAY")
      return {
        title: "Center closed on the preferred date",
        detail: `The center is closed${onDate}, so the patient's preferred ${preferredPeriodName} slot can't be scheduled that day.`,
      };
    if (reason === "PAST")
      return {
        title: "Preferred time has already passed",
        detail: `The ${preferredPeriodName} slot${onDate} has already passed for today${hoursText ? `. The center's operating hours are ${hoursText}` : ""}.`,
      };
    // OUT_OF_HOURS
    return {
      title: "Preferred time is outside operating hours",
      detail: `The patient's preferred ${preferredPeriodName} slot${onDate} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}.`,
    };
  };

  // Build the availability banner content. Prioritizes the SELECTED SERVICE DATE
  // (the actionable choice) once its slots have loaded — warning when that date
  // is closed or the preferred period falls outside its operating hours — and
  // otherwise falls back to the patient's PREFERRED slot status. Returns
  // `{ key, title, detail }` or null when nothing needs flagging. `key` is a
  // stable signature used to reset the dismiss state when the reason changes.
  const buildSlotBanner = () => {
    const selStr = formatDateYMD(serviceDate);
    if (serviceDate && centerSlots && centerSlots.date === selStr) {
      const dateLabel = formatYMDLabel(selStr);
      if (centerSlots.is_open === false) {
        return {
          key: `SVC|CLOSED|${selStr}`,
          title: "Center closed on the selected service date",
          detail: `The center is closed on ${dateLabel}. Please choose another service date.`,
        };
      }
      if (!getSlotWindowFrom(centerSlots)) {
        const hoursText = hoursTextFrom(centerSlots.operating_hours);
        return {
          key: `SVC|OOH|${selStr}`,
          title: "Selected service date is outside operating hours",
          detail: `The ${preferredPeriodName} slot on ${dateLabel} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}. Please choose another date or time.`,
        };
      }
      return null; // service date is valid → no banner
    }

    // No service date chosen yet → reflect the patient's preferred slot.
    const pref = evaluatePreferredSlotStatus();
    if (pref.loaded && !pref.open) {
      const msg = getPreferredSlotClosedMessage(pref.reason);
      return {
        key: `PREF|${pref.reason}|${order?.preferredDate}`,
        title: msg.title,
        detail: msg.detail,
      };
    }
    return null;
  };

  // Get time range based on preferred time slot.
  const getTimeRangeForSlot = (slot) => {
    // Prefer the live operating-hours window for the preferred period.
    const win = getPreferredSlotWindow();
    if (win)
      return {
        min: minutesToHHMM(win.startMin),
        max: minutesToHHMM(win.endMin),
      };
    if (!slot) return { min: "00:00", max: "23:59" };
    const s = slot.toUpperCase();
    if (s.includes("MORNING")) return { min: "08:00", max: "12:00" };
    if (s.includes("AFTERNOON")) return { min: "12:00", max: "17:00" };
    if (s.includes("EVENING")) return { min: "17:00", max: "19:30" };
    return { min: "00:00", max: "23:59" };
  };

  const generateTimeOptions = (slot) => {
    const { min, max } = getTimeRangeForSlot(slot);
    const [minH, minM] = min.split(":").map(Number);
    const [maxH, maxM] = max.split(":").map(Number);
    const opts = [];
    for (let h = minH; h <= maxH; h++) {
      for (let m = 0; m < 60; m += 30) {
        if (h === minH && m < minM) continue;
        if (h === maxH && m > maxM) break;
        const val = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
        opts.push({
          value: val,
          label: `${dh}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`,
        });
      }
    }
    return opts;
  };

  // Turn a "HH:MM" slot into a { value, label } option with a 12-hour label.
  const slotToOption = (hhmm) => {
    const [h, m] = String(hhmm).split(":").map(Number);
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h >= 12 ? "PM" : "AM";
    return {
      value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      label: `${displayHour}:${String(m).padStart(2, "0")} ${period}`,
    };
  };

  // Format a stored time (e.g. "10:00:00" or "10:00") into a 12-hour label for
  // display. Returns null for empty/invalid values so callers can fall back.
  const formatTime12h = (time) => {
    if (!time || typeof time !== "string") return null;
    const [h, m] = time.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h >= 12 ? "PM" : "AM";
    return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
  };

  // Low-level call to the center-slots endpoint for a given date (center = auth user).
  const requestCenterSlots = async (dateStr) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = `/api/v1/imaging/center-slots${dateStr ? `?date=${dateStr}` : ""}`;
      const res = await axiosInstance.get(url);
      return res?.data?.data || null;
    } catch (e) {
      return null;
    }
  };

  // Service-date-based fetch (Center Availability + Service Time). When
  // notifyIfClosed is true (officer picked the date), a closed day shows a toast.
  const fetchCenterSlots = async (dateStr, notifyIfClosed = false) => {
    const data = await requestCenterSlots(dateStr);
    if (data) {
      setCenterSlots(data);
      if (notifyIfClosed && data.is_open === false) {
        toast.error(
          "The center is closed on the selected date. Please choose another date.",
        );
      }
    }
  };

  // On load and whenever the service date changes, refresh the service-date slots.
  useEffect(() => {
    const dateStr = formatDateYMD(serviceDate) || order?.preferredDate || null;
    if (dateStr) {
      const notify = dateSelectedByUserRef.current;
      dateSelectedByUserRef.current = false;
      fetchCenterSlots(dateStr, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate, order?.preferredDate]);

  // Fetch the preferred-date slots ONCE (pinned to the patient's preferred date)
  // so the Preferred Time Slot badge/range never changes with the service date.
  useEffect(() => {
    const pd = order?.preferredDate || null;
    if (pd)
      requestCenterSlots(pd).then((data) => {
        if (data) setPreferredSlots(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.preferredDate]);

  // Service Time options come from the center-slots endpoint for the selected
  // service date (the patient's preferred period bucket — already clipped to
  // that day's operating hours). Falls back to the static generator until the
  // slots load; a closed day / preferred period with no slots yields no options.
  const buildTimeOptions = () => {
    const slotKey = (order?.preferredTimeSlot || "").toLowerCase();
    const bucket = centerSlots?.slots?.[slotKey];
    if (centerSlots) {
      let slots = Array.isArray(bucket?.slots) ? bucket.slots : [];
      // If the selected date is today, drop slots whose start time has already
      // passed (e.g. the morning slots are over by the afternoon → empty list).
      const todayStr = nowInDisplayZone().date; // Baghdad date
      if (centerSlots.date && centerSlots.date === todayStr) {
        const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
        slots = slots.filter((hhmm) => hhmmToMinutes(hhmm) > nowMinutes);
      }
      return slots.map(slotToOption);
    }
    // Not loaded yet — fall back to the previous static generation.
    return generateTimeOptions(order?.preferredTimeSlot);
  };

  const timeOptions = buildTimeOptions();

  // ── Local tests state ──────────────────────────────────────────────────────
  const [tests, setTests] = useState([]);
  const [showAddTestRow, setShowAddTestRow] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [currentTest, setCurrentTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    transportationFee: "0",
    location: "ON_SITE",
  });
  const [editingTestId, setEditingTestId] = useState(null);
  const [editingTest, setEditingTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    transportationFee: "0",
    location: "ON_SITE",
  });
  const [editingTestServiceId, setEditingTestServiceId] = useState("");
  const [isSavingTests, setIsSavingTests] = useState(false);

  // Order-level transportation fee state
  const [orderTransportationFee, setOrderTransportationFee] = useState("0");
  const [transportationFeeAdded, setTransportationFeeAdded] = useState(false);
  // True once the center has explicitly saved a 0 IQD fee in this view. The stored column
  // defaults to 0, so a refetch alone cannot tell "free transport" from "not entered yet"; this
  // keeps an explicit 0 from being reset to "not added" when the order is re-synced.
  const explicitZeroFeeRef = useRef(false);

  // Accept / Reject (first-received order) modal + loading state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acceptingOrder, setAcceptingOrder] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(false);

  // Reject reason dropdown — admin-configured reasons for this center, plus an
  // "Other" option that reveals the free-text box. Fetched when the modal opens.
  const [selectedRejectReasonId, setSelectedRejectReasonId] = useState("");
  const {
    options: rejectReasonOptions,
    loading: loadingRejectReasons,
    resolveReasonText,
  } = useRejectionReasons("imaging", showRejectModal);
  const isCustomRejectReason =
    selectedRejectReasonId === CUSTOM_REJECTION_REASON;

  // Single place to clear the dialog's inputs, used by every close path.
  const resetRejectForm = () => {
    setSelectedRejectReasonId("");
    setRejectReason("");
  };

  // A newly-received order must be Accepted by the center before tests can be
  // added / the invoice sent. Backend keeps status REQUEST_SUBMITTED and flips
  // the imaging_accepted flag. Until then only Accept/Reject buttons show; if
  // rejected the Test Details + Invoice sections stay hidden.
  const isImagingAccepted = order?.imagingAccepted === true;
  const isAwaitingCenterDecision =
    order?.status === "REQUEST_SUBMITTED" &&
    !isImagingAccepted &&
    !order?.patientConfirmationReceivedAt;
  const isOrderRejected = order?.status === "REJECTED";
  // Reason the officer gave when rejecting, shown in the "Order rejected" panel.
  const orderRejectionReason =
    order?.rejectionReason || order?.rejection_reason || "";
  const showTestAndInvoiceSections =
    !!order && !isAwaitingCenterDecision && !isOrderRejected;

  // Service Date / Time can only be edited after the center accepts the order,
  // and before scheduling is locked in by a later status. Until acceptance the
  // inputs stay disabled.
  const serviceScheduleLockedStatuses = [
    "INVOICE_RECEIVED",
    "AWAITING_PAYMENT",
    "ORDER_ACCEPTED",
    "SCHEDULED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
    "REJECTED",
  ];
  const isServiceDateTimeDisabled =
    !isImagingAccepted || serviceScheduleLockedStatuses.includes(order?.status);

  // The invoice has been sent to the patient: the order is waiting for payment
  // or has moved past it. From here on the service date / time are fixed, so the
  // operating-hours warnings (closed / outside hours / no time slots) are no
  // longer shown, even if the center later changes its operating hours.
  const invoiceSentStatuses = [
    "INVOICE_RECEIVED",
    "AWAITING_PAYMENT",
    "ORDER_ACCEPTED",
    "SCHEDULED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
  ];
  const isInvoiceSentToPatient = invoiceSentStatuses.includes(order?.status);
  // The service date / time can no longer change: the invoice was sent, or the
  // order is closed as REJECTED (by the center, or automatically when the patient
  // did not pay within the payment window after the invoice was sent). The
  // operating-hours warnings ask the officer to pick another date / time, which
  // is not possible here, so they stay hidden even if the hours change later.
  const isServiceScheduleFixed = isInvoiceSentToPatient || isOrderRejected;
  // Invoice sent and still waiting for the patient to pay: shown in the blue
  // "Invoice sent" panel at the top of the Order Details tab.
  const isAwaitingPatientPayment = ["INVOICE_RECEIVED", "AWAITING_PAYMENT"].includes(
    order?.status,
  );

  // Accept a newly-received order. Keeps status REQUEST_SUBMITTED (backend only
  // flips the imaging_accepted flag) and notifies the patient, then the officer
  // can add tests + send the invoice. Confirmed via a dialog.
  const confirmAcceptOrder = async () => {
    setAcceptingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(
        `/api/v1/imaging/orders/${id}/accept`,
      );
      if (response?.data?.status) {
        toast.success("Order accepted");
        setShowAcceptModal(false);
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(response?.data?.message || "Failed to accept order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept order");
    } finally {
      setAcceptingOrder(false);
    }
  };

  // Reject a newly-received order. Sets status REJECTED and notifies the patient
  // (bilingual, persisted) with the optional reason. Confirmed via a dialog.
  const confirmRejectOrder = async () => {
    // A preset reason submits its own text; "Other" submits the free-text box.
    const presetReason = resolveReasonText(selectedRejectReasonId);
    const finalReason = presetReason || rejectReason.trim();

    setRejectingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(
        `/api/v1/imaging/orders/${id}/reject`,
        {
          rejectionReason: finalReason || undefined,
        },
      );
      if (response?.data?.status) {
        toast.success("Order rejected");
        setShowRejectModal(false);
        resetRejectForm();
        navigate("/imaging_ordermanagement");
      } else {
        toast.error(response?.data?.message || "Failed to reject order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reject order");
    } finally {
      setRejectingOrder(false);
    }
  };
  const [showTransportationFeePopup, setShowTransportationFeePopup] = useState(false);
  const [tempTransportationFee, setTempTransportationFee] = useState("0");

  // Sync transportation fee from API when order loads
  useEffect(() => {
    if (order?.pricing?.transportationFee !== undefined) {
      const fee = String(order.pricing.transportationFee);
      setOrderTransportationFee(fee);
      // An explicit 0 IQD fee stays "added".
      const syncedFee = parseFloat(fee) || 0;
      setTransportationFeeAdded(
        syncedFee > 0 || (syncedFee === 0 && explicitZeroFeeRef.current),
      );
    }
  }, [order?.pricing?.transportationFee]);

  // ── Fees state ─────────────────────────────────────────────────────────────
  // Default 0, NOT a hardcoded rate: the admin fee is configured in the admin panel and
  // nowhere else, so until that configuration is read (or when it is inactive) no admin fee
  // applies. The old 0.5% default was 10× off the configured 4% for imaging and would show a
  // phantom charge on any order whose fee configuration had not loaded yet.
  const [adminFeePercentage, setAdminFeePercentage] = useState(0);

  const fetchImagingFees = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/fees/configuration`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status && res.data?.data) {
        const { admin_fee } = res.data.data;
        // Respect the is_active flag: an inactive configuration means no admin fee.
        if (admin_fee?.is_active && admin_fee?.percentage !== undefined)
          setAdminFeePercentage(parseFloat(admin_fee.percentage) || 0);
        else setAdminFeePercentage(0);
      }
    } catch (e) {
      /* use defaults */
    }
  };

  const fetchImagingCenterProfile = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingProfile}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      console.log("[INVOICE] Profile response:", res.data);
      if (res.data?.status && res.data?.data) {
        console.log("[INVOICE] Profile data:", res.data.data);
        console.log(
          "[INVOICE] Center name:",
          res.data.data.imaging_center_name_english,
        );
        setImagingCenterProfile(res.data.data);
      }
    } catch (e) {
      console.error("[INVOICE] Profile fetch error:", e);
    }
  };

  const parsePrice = (v) => {
    if (!v) return 0;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  };

  // The whole order shares a single service location. Seed it from tests that
  // are already added; otherwise fall back to the patient's preferred location.
  const getEffectiveLocation = () => {
    const source = tests.length > 0 ? tests : order?.tests || [];
    const existingWithLocation = source.find((t) => t?.location);
    let loc = existingWithLocation
      ? normalizeLocationCode(existingWithLocation.location)
      : normalizeLocationCode(
          order?.preferredLocation || order?.preferredLocationRaw,
        );
    // Clamp to what the provider's consultation type permits.
    const allowed = allowedLocationsFromConsultation(consultationType);
    if (!allowed.includes(loc)) loc = allowed[0];
    return loc;
  };

  // Changing the location on any single test applies it to every test in the
  // order, keeping the add-row and edit-row selections in sync as well.
  const applyLocationToAllTests = (newLocation) => {
    const location = normalizeLocationCode(newLocation);
    setTests((prev) => prev.map((t) => ({ ...t, location })));
    setCurrentTest((prev) => ({ ...prev, location }));
    setEditingTest((prev) => ({ ...prev, location }));
  };

  const handleServiceSelection = (serviceId) => {
    setSelectedServiceId(serviceId);
    if (!serviceId) return;
    const svc = imagingServices.find((s) => String(s.id) === String(serviceId));
    if (svc) {
      // Location follows the order's effective location (preferred location, or
      // whatever the already-added tests use) rather than the service's value.
      const location = getEffectiveLocation();
      setCurrentTest({
        testName: svc.serviceName || svc.testName || "",
        serviceCode: svc.serviceCode || "",
        price: svc.price || "",
        transportationFee: "0",
        location,
      });
    }
  };

  const addTestToList = () => {
    const testName = currentTest.testName?.toString().trim();
    const serviceCode = currentTest.serviceCode?.toString().trim();
    const price = currentTest.price?.toString().trim();
    if (!testName || !serviceCode || !price) {
      toast.error(
        "Please fill in all required fields (Test Name, Service Code, Price)",
      );
      return;
    }
    if (
      tests.some(
        (t) => t.testName === testName && t.serviceCode === serviceCode,
      )
    ) {
      toast.error("This test is already added to the list");
      return;
    }
    setTests((prev) => [...prev, { ...currentTest, id: Date.now() }]);
    setCurrentTest({
      testName: "",
      serviceCode: "",
      price: "",
      transportationFee: "0",
      location: getEffectiveLocation(),
    });
    setSelectedServiceId("");
    setShowAddTestRow(false);
    toast.success("Test added to list");
  };

  const removeTestFromList = (testId) =>
    setTests((prev) => prev.filter((t) => t.id !== testId));

  // Services already present in the Test Details list (locally added + saved on the
  // order) should not appear again in the "Add Test" dropdown.
  const addedServiceCodes = new Set(
    [...(tests || []), ...(order?.tests || [])]
      .map((t) => (t.serviceCode || t.service_code || "").toString())
      .filter(Boolean),
  );
  const availableImagingServices = (imagingServices || []).filter(
    (s) =>
      !addedServiceCodes.has(
        (s.serviceCode || s.service_code || "").toString(),
      ),
  );
  // For the edit row, keep the test currently being edited selectable.
  const availableImagingServicesForEdit = (imagingServices || []).filter(
    (s) => {
      const code = (s.serviceCode || s.service_code || "").toString();
      return (
        !addedServiceCodes.has(code) ||
        code === (editingTest?.serviceCode || "").toString()
      );
    },
  );

  const startEditingTest = (test) => {
    setEditingTestId(test.id);
    setEditingTest({
      testName: test.testName,
      serviceCode: test.serviceCode,
      price: test.price,
      transportationFee: test.transportationFee || "0",
      location: test.location,
    });
    const svc = imagingServices.find(
      (s) => s.serviceName === test.testName || s.testName === test.testName,
    );
    setEditingTestServiceId(svc ? String(svc.id) : "");
  };

  const saveEditedTest = () => {
    if (!editingTest.testName || !editingTest.price) {
      toast.error("Please fill in all required fields");
      return;
    }
    setTests((prev) =>
      prev.map((t) => (t.id === editingTestId ? { ...t, ...editingTest } : t)),
    );
    setEditingTestId(null);
    setEditingTestServiceId("");
  };

  const cancelEditingTest = () => {
    setEditingTestId(null);
    setEditingTestServiceId("");
  };

  const handleEditServiceSelection = (serviceId) => {
    setEditingTestServiceId(serviceId);
    if (!serviceId) return;
    const svc = imagingServices.find((s) => String(s.id) === String(serviceId));
    if (svc) {
      const location = getEffectiveLocation();
      setEditingTest((p) => ({
        ...p,
        testName: svc.serviceName || svc.testName || "",
        serviceCode: svc.serviceCode || "",
        price: svc.price || "",
        location,
      }));
    }
  };

  const handleGenerateInvoice = async () => {
    if (!id) {
      toast.error("Order ID is missing");
      return;
    }
    const displayTests = tests.length > 0 ? tests : order?.tests || [];
    if (displayTests.length === 0) {
      toast.error("Please add at least one test before generating invoice");
      return;
    }

    // If tests already exist from API (post-split), validate schedule then generate invoice
    if (tests.length === 0 && (order?.tests || []).length > 0) {
      // Check if already scheduled
      const alreadyScheduled = order?.scheduledDate && order?.scheduledTime;

      if (!alreadyScheduled) {
        if (!serviceDate) {
          toast.error("Please select a service date");
          return;
        }
        if (blockServiceDateBeforePreferred()) return;
        if (!serviceTime?.trim()) {
          toast.error("Please select a service time");
          return;
        }
      }

      setIsSavingTests(true);
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        // Schedule if not already scheduled
        if (!alreadyScheduled) {
          // The centre works on Baghdad time (fixed), whatever this device's clock says.
          const timezone = PLATFORM_TIMEZONE;
          // Use the LOCAL calendar date, not toISOString() — the latter converts
          // the local-midnight Date to UTC and rolls back a day for timezones
          // ahead of UTC (e.g. Baghdad UTC+3 turned 29th → 28th).
          const scheduledDate = formatDateYMD(serviceDate);
          const schedRes = await axios.put(
            `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/schedule`,
            { scheduledDate, scheduledTime: serviceTime, timezone },
            { headers },
          );
          if (!schedRes.data?.status) {
            toast.error(
              schedRes.data?.message || "Failed to schedule appointment",
            );
            setIsSavingTests(false);
            return;
          }
          toast.success("Appointment scheduled successfully");
        }

        const invRes = await axios.post(
          `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/generate-invoice`,
          {},
          { headers },
        );
        if (invRes.data?.status) {
          toast.success("Invoice generated and sent to patient successfully");
          dispatch(fetchImagingOrderDetails(id));
        } else {
          toast.error(invRes.data?.message || "Failed to generate invoice");
        }
      } catch (e) {
        toast.error(e.response?.data?.message || "Failed to generate invoice");
      } finally {
        setIsSavingTests(false);
      }
      return;
    }

    if (!serviceDate) {
      toast.error("Please select a service date");
      return;
    }
    if (blockServiceDateBeforePreferred()) return;
    if (!serviceTime?.trim()) {
      toast.error("Please select a service time");
      return;
    }

    setIsSavingTests(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      // The centre works on Baghdad time (fixed), whatever this device's clock says.
      const timezone = PLATFORM_TIMEZONE;
      // Use the LOCAL calendar date, not toISOString() — the latter converts the
      // local-midnight Date to UTC and rolls back a day for timezones ahead of
      // UTC (e.g. Baghdad UTC+3 turned 29th → 28th).
      const scheduledDate = formatDateYMD(serviceDate);

      // Step 1: Add tests
      const addRes = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/add-tests`,
        {
          tests: tests.map((t) => ({
            testName: t.testName,
            serviceCode: t.serviceCode,
            price: parseFloat(t.price),
            transportationFee: 0,
            location: t.location || "ON_SITE",
          })),
          transportationFee: tests.some((t) => t.location === "HOME_VISIT")
            ? parseFloat(orderTransportationFee) || 0
            : 0,
        },
        { headers },
      );
      if (!addRes.data?.status) {
        toast.error(addRes.data?.message || "Failed to add tests");
        return;
      }
      toast.success("Tests added successfully");

      const schedRes = await axios.put(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/schedule`,
        { scheduledDate, scheduledTime: serviceTime, timezone },
        { headers },
      );
      if (!schedRes.data?.status) {
        toast.error(schedRes.data?.message || "Failed to schedule appointment");
        return;
      }
      toast.success("Appointment scheduled successfully");

      // Step 3: Generate invoice
      const invRes = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/generate-invoice`,
        {},
        { headers },
      );
      if (invRes.data?.status) {
        toast.success("Invoice generated and sent to patient successfully");
        setTests([]);
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(invRes.data?.message || "Failed to generate invoice");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to process request");
    } finally {
      setIsSavingTests(false);
    }
  };

  const [uploadingFile, setUploadingFile] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [imagingCenterProfile, setImagingCenterProfile] = useState(null);

  const handleMultipleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    // Check if report is already sent
    if (order?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Upload is disabled.",
      );
      event.target.value = ""; // Clear the file input
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size must be less than 25MB");
      return;
    }
    if (!id) {
      toast.error("No order ID available");
      return;
    }
    if (uploadingFile) {
      toast.warning("Upload already in progress");
      return;
    }

    setUploadingFile(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const formData = new FormData();
      formData.append("file", file);
      const fileSizeMB = file.size / (1024 * 1024);
      const endpoint =
        fileSizeMB > 10
          ? `${process.env.REACT_APP_API_URL}${APP_PATH.uploadResultsMultipart}/${id}/upload-results-multipart`
          : `${process.env.REACT_APP_API_URL}${APP_PATH.uploadResults}/${id}/upload-results`;

      const res = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data?.status) {
        toast.success("Test results uploaded successfully");
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(res.data?.message || "Failed to upload test results");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to upload test results");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileClick = async (fileId, fileName) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      toast.info("Opening file...");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getFilePresignedUrl}/${id}/file/${fileId}/presigned-url`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      if (result.status && result.data?.presignedUrl) {
        window.open(result.data.presignedUrl, "_blank");
      } else {
        toast.error(result.message || "Failed to open file");
      }
    } catch (e) {
      toast.error("Failed to open file");
    }
  };

  const handleFileDelete = async (fileId, fileName) => {
    // Check if report is already sent
    if (order?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. File deletion is disabled.",
      );
      return;
    }

    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.deleteTestResultFile}/${id}/file/${fileId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      if (result.status) {
        toast.success("File deleted");
        dispatch(fetchImagingOrderDetails(id));
      } else toast.error(result.message || "Failed to delete file");
    } catch (e) {
      toast.error("Failed to delete file");
    }
  };

  // ── Result link (second way to deliver results, next to the PDF upload) ──────
  // Saved on the order; "Share with Patient & Doctor" then sends the same patient
  // push and doctor notification as for uploaded files.
  const [showResultLinkModal, setShowResultLinkModal] = useState(false);
  const [resultLinkInput, setResultLinkInput] = useState("");
  const [resultLinkError, setResultLinkError] = useState("");
  const [savingResultLink, setSavingResultLink] = useState(false);

  const openResultLinkModal = () => {
    if (order?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Adding a link is disabled.",
      );
      return;
    }
    setResultLinkInput(order?.resultLink || "");
    setResultLinkError("");
    setShowResultLinkModal(true);
  };

  const closeResultLinkModal = () => {
    if (savingResultLink) return;
    setShowResultLinkModal(false);
  };

  const handleSaveResultLink = async () => {
    if (savingResultLink) return;
    const link = resultLinkInput.trim();
    if (!link) {
      setResultLinkError("Please paste the result link.");
      return;
    }
    if (!isValidResultLink(link)) {
      setResultLinkError(
        `Enter a valid link that starts with http:// or https:// (up to ${RESULT_LINK_MAX_LENGTH} characters).`,
      );
      return;
    }
    if (!id) {
      toast.error("No order ID available");
      return;
    }

    setSavingResultLink(true);
    setResultLinkError("");
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingResultLink}/${id}/result-link`,
        { link },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.data?.status) {
        toast.success("Result link saved");
        setShowResultLinkModal(false);
        dispatch(fetchImagingOrderDetails(id));
      } else {
        setResultLinkError(
          res.data?.message || "Failed to save the result link",
        );
      }
    } catch (e) {
      setResultLinkError(
        e.response?.data?.message || "Failed to save the result link",
      );
    } finally {
      setSavingResultLink(false);
    }
  };

  const handleRemoveResultLink = async () => {
    if (order?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Removing the link is disabled.",
      );
      return;
    }
    if (!window.confirm("Remove the result link? This cannot be undone."))
      return;
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingResultLink}/${id}/result-link`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status) {
        toast.success("Result link removed");
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(res.data?.message || "Failed to remove the result link");
      }
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Failed to remove the result link",
      );
    }
  };

  const handleShareWithPatient = async () => {
    if (!id) {
      toast.error("No order ID available");
      return;
    }

    // Check if report is already sent
    if (order?.reportStatus === "SENT") {
      toast.info("Report has already been shared with the patient");
      return;
    }

    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingResendReport}/${id}/resend`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.data?.status) {
        toast.success("Results shared with patient successfully");
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(res.data?.message || "Failed to share results");
      }
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Failed to share results with patient",
      );
    }
  };
  const fmt = (d) => {
    if (!d) return "-";
    try {
      return tzDateNumeric(new Date(d));
    } catch {
      return d;
    }
  };
  const fmtStatus = (s) => {
    if (!s) return "";
    return s
      .split(/[_\s-]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const handlePrintInvoice = async () => {
    if (!imagingCenterProfile) {
      await fetchImagingCenterProfile();
    }
    setShowInvoiceModal(true);
  };

  // Helper to check if order is home visit
  const isHomeVisitOrder = () => {
    const loc = order?.preferredLocation || order?.preferredLocationRaw || "";
    return loc === "Home Visit" || loc === "HOME_VISIT";
  };
  // On-Site vs Home Visit is decided by the TEST locations (there is no split
  // case anymore, so all tests share one location): if no test is a HOME_VISIT
  // the order is On-Site — technician assignment/dispatch is skipped and the
  // on-site "mark completed" workflow is shown instead. Falls back to the order's
  // preferred location only when no tests have been added yet.
  const isOnSiteOrder = () => {
    const displayTests = tests && tests.length > 0 ? tests : order?.tests || [];
    if (displayTests.length > 0) {
      return displayTests.every(
        (t) => (t.location || "ON_SITE") !== "HOME_VISIT",
      );
    }
    return !isHomeVisitOrder();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Pending":
      case "COD":
        return "status-cod";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
      case "On-Site":
      case "Visit Center":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      default:
        return "";
    }
  };

  const EditIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.9525 3.04199H13.25C13.4157 3.04199 13.5747 3.10784 13.6919 3.22505C13.8091 3.34226 13.875 3.50123 13.875 3.66699C13.875 3.83275 13.8091 3.99172 13.6919 4.10893C13.5747 4.22614 13.4157 4.29199 13.25 4.29199H12C10.0183 4.29199 8.59496 4.29366 7.51163 4.43866C6.44579 4.58199 5.80329 4.85533 5.32829 5.32949C4.85413 5.80449 4.58163 6.44616 4.43829 7.51199C4.29329 8.59532 4.29163 10.0187 4.29163 12.0003C4.29163 13.982 4.29329 15.4053 4.43829 16.4887C4.58163 17.5545 4.85496 18.197 5.32913 18.672C5.80413 19.1462 6.44579 19.4187 7.51163 19.562C8.59496 19.707 10.0183 19.7087 12 19.7087C13.9816 19.7087 15.405 19.707 16.4883 19.562C17.5541 19.4187 18.1966 19.1453 18.6716 18.6712C19.1458 18.1962 19.4183 17.5545 19.5616 16.4887C19.7066 15.4053 19.7083 13.982 19.7083 12.0003V10.7503C19.7083 10.5846 19.7741 10.4256 19.8913 10.3084C20.0086 10.1912 20.1675 10.1253 20.3333 10.1253C20.4991 10.1253 20.658 10.1912 20.7752 10.3084C20.8924 10.4256 20.9583 10.5846 20.9583 10.7503V12.0478C20.9583 13.972 20.9583 15.4795 20.8 16.6562C20.6383 17.8595 20.3 18.8095 19.555 19.5553C18.8091 20.3012 17.8591 20.6387 16.655 20.8003C15.4791 20.9587 13.9716 20.9587 12.0475 20.9587H11.9525C10.0283 20.9587 8.52079 20.9587 7.34413 20.8003C6.14079 20.6387 5.19079 20.3003 4.44496 19.5553C3.69913 18.8095 3.36163 17.8595 3.19996 16.6553C3.04163 15.4795 3.04163 13.972 3.04163 12.0478V11.9528C3.04163 10.0287 3.04163 8.52116 3.19996 7.34449C3.36163 6.14116 3.69996 5.19116 4.44496 4.44533C5.19079 3.69949 6.14079 3.36199 7.34496 3.20033C8.52079 3.04199 10.0283 3.04199 11.9525 3.04199ZM15.975 3.89699C16.5249 3.3622 17.2633 3.06536 18.0303 3.07068C18.7974 3.076 19.5316 3.38304 20.074 3.92541C20.6165 4.46777 20.9237 5.20187 20.9292 5.96895C20.9347 6.73603 20.638 7.47444 20.1033 8.02449L14.5633 13.5653C14.2541 13.8737 14.06 14.0687 13.8441 14.237C13.5894 14.436 13.3136 14.6065 13.0216 14.7453C12.7741 14.8628 12.5133 14.9495 12.0991 15.0878L9.67829 15.8945C9.45927 15.9674 9.22427 15.978 8.99961 15.9249C8.77494 15.8718 8.56949 15.7573 8.40626 15.594C8.24302 15.4308 8.12846 15.2253 8.07539 15.0007C8.02232 14.776 8.03285 14.541 8.10579 14.322L8.91246 11.9012C9.05079 11.4862 9.13746 11.2262 9.25496 10.9787C9.39496 10.6864 9.5644 10.4123 9.76329 10.1562C9.93163 9.93949 10.1258 9.74616 10.435 9.43699L15.975 3.89699ZM19.2191 4.78033C18.9061 4.46747 18.4817 4.29172 18.0391 4.29172C17.5966 4.29172 17.1721 4.46747 16.8591 4.78033L16.5458 5.09449C16.5636 5.17505 16.5908 5.26894 16.6275 5.37616C16.7466 5.72033 16.9733 6.17366 17.4 6.60033C17.8154 7.01596 18.3359 7.31115 18.9058 7.45449L19.2191 7.14116C19.532 6.82815 19.7077 6.40371 19.7077 5.96116C19.7077 5.5186 19.532 5.09333 19.2191 4.78033ZM17.9208 8.44033C17.3961 8.21408 16.9192 7.88998 16.5158 7.48533C16.1111 7.08187 15.787 6.60504 15.5608 6.08033L11.3483 10.292C11.0008 10.6395 10.865 10.777 10.7483 10.9253C10.6066 11.1093 10.4846 11.3076 10.3841 11.517C10.3025 11.6862 10.2408 11.8695 10.085 12.3353L9.72579 13.4145L10.5858 14.2753L11.665 13.9153C12.1316 13.7595 12.3141 13.6978 12.4841 13.617C12.6941 13.517 12.8913 13.395 13.0758 13.2512C13.2233 13.1353 13.3608 12.9995 13.7083 12.6528L17.9208 8.44033Z"
        fill="#053345"
      />
    </svg>
  );

  // Determine which tabs to show based on order status (same logic as lab app)
  const tabs = (() => {
    const orderStatus = order?.status;
    const isOnSite = isOnSiteOrder();

    // On-site bookings: show Technician Detail tab for active/in-progress/completed statuses
    const onsiteTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "IN_PROGRESS",
      "COMPLETED",
    ];
    // Home visit bookings: show Technician Detail tab for scheduled / dispatched /
    // in-progress / completed statuses (read-only once the visit is completed).
    const homeVisitTechnicianTabStatuses = [
      "ORDER_ACCEPTED",
      "TECHNICIAN_ASSIGNED",
      "SCHEDULED",
      "TECHNICIAN_DISPATCHED",
      "IN_PROGRESS",
      "COMPLETED",
    ];

    const showTechnicianTab = isOnSite
      ? onsiteTechnicianTabStatuses.includes(orderStatus)
      : homeVisitTechnicianTabStatuses.includes(orderStatus);

    // For COMPLETED home-visit, show Technician Detail and Results & Reports
    if (orderStatus === "COMPLETED" && !isOnSite) {
      return ["Order Details", "Technician Detail", "Results & Reports"];
    }

    // For COMPLETED on-site, show Technician Detail and Results & Reports
    if (orderStatus === "COMPLETED" && isOnSite) {
      return ["Order Details", "Technician Detail", "Results & Reports"];
    }

    // Show technician tab for applicable statuses
    if (showTechnicianTab) {
      return ["Order Details", "Technician Detail"];
    }

    // For all other statuses, show only Order Details
    return ["Order Details"];
  })();

  // Technician management state
  const [availableTechnicians, setAvailableTechnicians] = useState([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  // Loading state for the "Send Patient Details via WhatsApp" (assign technician) button
  const [assigningTechnician, setAssigningTechnician] = useState(false);
  // Loading states for the on-site Order Actions buttons (Sample Collected /
  // Mark as Collected-Completed / Job Done) so each shows an in-button spinner.
  const [isSampleCollecting, setIsSampleCollecting] = useState(false);
  const [isCompletingJob, setIsCompletingJob] = useState(false);
  const [gender, setGender] = useState("");

  // Auto-set gender from preferred technician gender when order loads
  useEffect(() => {
    if (order?.preferredTechnicianGender) {
      const pref = order.preferredTechnicianGender.toUpperCase();
      if (pref === "MALE") setGender("male");
      else if (pref === "FEMALE") setGender("female");
    }
  }, [order?.preferredTechnicianGender]);

  // Fetch available technicians when gender changes
  useEffect(() => {
    if (id && gender) {
      fetchAvailableTechnicians();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender]);

  const fetchAvailableTechnicians = async () => {
    if (!id) return;
    setLoadingTechnicians(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/available-technicians`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status) {
        setAvailableTechnicians(res.data.data?.technicians || []);
      }
    } catch (e) {
      toast.error("Failed to fetch technicians");
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const handleAssignTechnician = async () => {
    if (!id) {
      toast.error("No order ID available");
      return;
    }
    if (!selectedTechnicianId) {
      toast.error(
        t(
          "imagingAppointmentDetail.selectTechnicianBeforeProceeding",
          "Please select a technician before proceeding.",
        ),
      );
      return;
    }
    setAssigningTechnician(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/assign-technician`,
        { technicianId: parseInt(selectedTechnicianId) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.data?.status) {
        const data = res.data.data;

        // Show success message with WhatsApp notification status
        if (data?.messagingSent) {
          toast.success(
            `Technician assigned successfully! WhatsApp notification sent via ${data.messagingMethod}`,
          );
        } else {
          toast.success("Technician assigned successfully");
          if (data?.whatsappLink) {
            toast.info(
              "WhatsApp link generated. You can manually share patient details if needed.",
            );
          }
        }

        // Log enriched data for debugging
        console.log("Technician assignment response:", {
          whatsappLink: data?.whatsappLink,
          messagingSent: data?.messagingSent,
          messagingMethod: data?.messagingMethod,
          patient: data?.patient,
          tests: data?.tests,
          navigationLink: data?.navigationLink,
        });

        dispatch(fetchImagingOrderDetails(id));
        await fetchAvailableTechnicians();
      } else {
        toast.error(res.data?.message || "Failed to assign technician");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to assign technician");
    } finally {
      setAssigningTechnician(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const endpoint =
        newStatus === "IN_PROGRESS"
          ? `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/start-process`
          : `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/complete`;

      const res = await axios.put(
        endpoint,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status) {
        toast.success(
          newStatus === "IN_PROGRESS"
            ? "Process started successfully"
            : "Order marked as completed",
        );
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(res.data?.message || "Failed to update status");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update status");
    }
  };

  const handleMarkCollected = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/mark-collected`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status) {
        toast.success("COD payment collected and order completed");
        dispatch(fetchImagingOrderDetails(id));
      } else {
        toast.error(res.data?.message || "Failed to collect payment");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to collect payment");
    }
  };

  const isCod = order?.paymentMethod?.toUpperCase() === "COD";

  return (
    <LabDashboardLayout
      currentPage="lab_ordermanagement"
      onNavigate={() => {}}
      onLogout={onLogout}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles["page-header"]}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <FaArrowLeftLong />
            </button>
            <h2>Appointment Detail</h2>
          </div>
          <div className={styles["apt-appointments-section"]}>
            <div className={`${styles["apt-section-header"]} mb-2`}>
              <h2>
                Appointment ID: <b>{order?.bookingNumber || id}</b>{" "}
                <span className={styles["tag-received"]}>
                  {fmtStatus(order?.status)}
                </span>{" "}
                {order?.isUrgent && (
                  <span className={styles["tag-urgent"]}>Urgent</span>
                )}
              </h2>
              <div className={styles["apt-top-actions"]}>
                <div style={{ display: "inline-flex", alignItems: "center" }}>
                  {/* Center-action SLA — visible only while awaiting the imaging
                      center's action. Once the invoice is sent (status leaves the
                      waiting set) the timer stops and disappears. */}
                  <SlaTimer
                    variant="detail"
                    dueAt={order?.slaDueAt}
                    isUrgent={order?.slaIsUrgent}
                    status={order?.status}
                    visibleStatuses={["REQUEST_SUBMITTED"]}
                  />
                </div>
                {[
                  "ORDER_ACCEPTED",
                  "TECHNICIAN_ASSIGNED",
                  "TECHNICIAN_DISPATCHED",
                  "IN_PROGRESS",
                  "COMPLETED",
                ].includes(order?.status) && (
                  <button
                    onClick={handlePrintInvoice}
                    className={styles["wallet-withdraw-btn"]}
                  >
                    <FiPrinter size={16} /> Print Invoice
                  </button>
                )}
                <button
                  className={styles["chat-icon-btn"]}
                  onClick={() => setIsChatOpen((p) => !p)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0C4.477 0 0 4.477 0 10C0 11.6 0.376 13.112 1.043 14.453C1.221 14.809 1.28 15.216 1.177 15.601L0.582 17.827C0.52307 18.0473 0.523124 18.2792 0.582155 18.4995C0.641186 18.7197 0.757118 18.9206 0.918315 19.0819C1.07951 19.2432 1.2803 19.3593 1.50053 19.4184C1.72075 19.4776 1.95267 19.4778 2.173 19.419L4.399 18.823C4.78538 18.7254 5.19414 18.7727 5.548 18.956C6.93095 19.6446 8.45512 20.002 10 20ZM6 11.25C5.80109 11.25 5.61032 11.329 5.46967 11.4697C5.32902 11.6103 5.25 11.8011 5.25 12C5.25 12.1989 5.32902 12.3897 5.46967 12.5303C5.61032 12.671 5.80109 12.75 6 12.75H11.5C11.6989 12.75 11.8897 12.671 12.0303 12.5303C12.171 12.3897 12.25 12.1989 12.25 12C12.25 11.8011 12.171 11.6103 12.0303 11.4697C11.8897 11.329 11.6989 11.25 11.5 11.25H6ZM5.25 8.5C5.25 8.30109 5.32902 8.11032 5.46967 7.96967C5.61032 7.82902 5.80109 7.75 6 7.75H14C14.1989 7.75 14.3897 7.82902 14.5303 7.96967C14.671 8.11032 14.75 8.30109 14.75 8.5C14.75 8.69891 14.671 8.88968 14.5303 9.03033C14.3897 9.17098 14.1989 9.25 14 9.25H6C5.80109 9.25 5.61032 9.17098 5.46967 9.03033C5.32902 8.88968 5.25 8.69891 5.25 8.5Z"
                      fill="#2B7FFF"
                    />
                  </svg>
                </button>
                {isChatOpen && (
                  <Chat
                    onClose={() => setIsChatOpen(false)}
                    patientUserId={
                      order?.patientId ||
                      order?.patient_id ||
                      order?.patient?.userId ||
                      order?.patient?.user_id ||
                      order?.patient?.id
                    }
                    bookingId={
                      id || order?.bookingId || order?.booking_id || order?.id
                    }
                    title={order?.patient?.name || "Chat"}
                    subtitle={
                      order?.bookingNumber || order?.booking_number || ""
                    }
                  />
                )}
              </div>
            </div>

            <div className={styles["patient-appointment-tabs"]}>
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`${styles["patient-tab"]} ${activeTab === tab ? styles["active"] : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "Technician Detail" && isOnSiteOrder()
                    ? "Process"
                    : tab}
                </button>
              ))}
            </div>

            {activeTab === "Order Details" && (
              <div className={styles["patient-tab-content"]}>
                {/* Acceptance required: a newly received order must be accepted
                    (or rejected) before the center can add tests, set the
                    service date/time or send the invoice. Shown first so the
                    decision is the first thing the officer sees. */}
                {isAwaitingCenterDecision && (
                  <section
                    className={styles["acceptance-panel"]}
                    aria-labelledby="acceptance-panel-title"
                  >
                    <div className={styles["acceptance-panel-body"]}>
                      <span
                        className={styles["acceptance-panel-icon"]}
                        aria-hidden="true"
                      >
                        <FaInfoCircle size={16} />
                      </span>
                      <div>
                        <h3
                          id="acceptance-panel-title"
                          className={styles["acceptance-panel-title"]}
                        >
                          {t(
                            "imagingAppointmentDetail.acceptanceRequiredTitle",
                            "Acceptance required",
                          )}
                        </h3>
                        <p className={styles["acceptance-panel-text"]}>
                          {t(
                            "imagingAppointmentDetail.acceptanceRequiredMessage",
                            "This is a new order request. Review the patient details, prescription and documents below, then accept or reject it. You can't add tests, set the service date and time, or send the invoice until the order is accepted.",
                          )}
                        </p>
                        <ul className={styles["acceptance-panel-list"]}>
                          <li>
                            <strong>
                              {t("imagingAppointmentDetail.accept", "Accept")}:
                            </strong>{" "}
                            {t(
                              "imagingAppointmentDetail.acceptanceAcceptHelp",
                              "your center takes this order. The patient is notified and you can continue with tests and the invoice.",
                            )}
                          </li>
                          <li>
                            <strong>
                              {t("imagingAppointmentDetail.reject", "Reject")}:
                            </strong>{" "}
                            {t(
                              "imagingAppointmentDetail.acceptanceRejectHelp",
                              "the order is declined and moved to the Rejected tab. The patient is notified.",
                            )}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div
                      className={`${dialogStyles["labDialog_wrapper"]} ${styles["acceptance-panel-actions"]}`}
                    >
                      <button
                        type="button"
                        className={`${dialogStyles["btn-1"]} ${styles["acceptance-panel-btn"]}`}
                        onClick={() => setShowAcceptModal(true)}
                        // Same height as the outlined Reject button.
                        style={{ border: "1px solid transparent" }}
                      >
                        <Check size={16} aria-hidden="true" />{" "}
                        {t("imagingAppointmentDetail.acceptOrder", "Accept Order")}
                      </button>
                      <button
                        type="button"
                        className={`${dialogStyles["btn-outlined-1"]} ${styles["acceptance-panel-btn"]}`}
                        onClick={() => {
                          setRejectReason("");
                          setShowRejectModal(true);
                        }}
                      >
                        {t("imagingAppointmentDetail.rejectOrder", "Reject Order")}
                      </button>
                    </div>
                  </section>
                )}

                {/* Order rejected: the red counterpart of the "Acceptance
                    required" panel, in the same place. States that the order
                    was rejected and shows the reason the officer gave. */}
                {isOrderRejected && (
                  <section
                    className={styles["rejection-panel"]}
                    aria-labelledby="rejection-panel-title"
                  >
                    <span
                      className={styles["rejection-panel-icon"]}
                      aria-hidden="true"
                    >
                      <IoClose size={18} />
                    </span>
                    <div className={styles["rejection-panel-body"]}>
                      <h3
                        id="rejection-panel-title"
                        className={styles["rejection-panel-title"]}
                      >
                        {t(
                          "imagingAppointmentDetail.rejectedTitle",
                          "Order rejected",
                        )}
                      </h3>
                      <p className={styles["rejection-panel-text"]}>
                        {t(
                          "imagingAppointmentDetail.rejectedMessage",
                          "This order was rejected and the patient has been notified.",
                        )}
                      </p>
                      <div className={styles["rejection-panel-reason"]}>
                        <span className={styles["rejection-panel-reason-label"]}>
                          {t(
                            "imagingAppointmentDetail.rejectionReasonLabel",
                            "Reason for rejection",
                          )}
                        </span>
                        {orderRejectionReason ? (
                          <p className={styles["rejection-panel-reason-text"]}>
                            {orderRejectionReason}
                          </p>
                        ) : (
                          <p
                            className={`${styles["rejection-panel-reason-text"]} ${styles["rejection-panel-reason-empty"]}`}
                          >
                            {t(
                              "imagingAppointmentDetail.noRejectionReason",
                              "No reason was given.",
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                {/* Invoice sent: the blue counterpart of the panels above, in
                    the same place. Shown while the invoice is with the patient
                    and the payment is still pending. */}
                {isAwaitingPatientPayment && (
                  <section
                    className={styles["invoice-sent-panel"]}
                    aria-labelledby="invoice-sent-panel-title"
                  >
                    <span
                      className={styles["invoice-sent-panel-icon"]}
                      aria-hidden="true"
                    >
                      <FaFileInvoice size={15} />
                    </span>
                    <div className={styles["invoice-sent-panel-body"]}>
                      <h3
                        id="invoice-sent-panel-title"
                        className={styles["invoice-sent-panel-title"]}
                      >
                        {t(
                          "imagingAppointmentDetail.invoiceSentTitle",
                          "Invoice sent",
                        )}
                      </h3>
                      <p className={styles["invoice-sent-panel-text"]}>
                        {t(
                          "imagingAppointmentDetail.invoiceSentMessage",
                          "The invoice has been sent to the patient. Waiting for the patient to complete the payment.",
                        )}
                      </p>
                    </div>
                  </section>
                )}

                {/* Patient Information */}
                <div className={styles["white-card"]}>
                  <div
                    className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                  >
                    <h2>Patient Information</h2>
                  </div>

                  {/* Out-of-operating-hours banner: shown when the patient's
                      preferred date/slot falls outside the center's operating
                      hours (closed day, period out of hours, or already passed). */}
                  {(() => {
                    // Once the schedule is fixed (invoice sent or order rejected), a
                    // later change to the operating hours no longer raises this banner.
                    if (isServiceScheduleFixed) return null;
                    const banner = buildSlotBanner();
                    if (!banner || banner.key === dismissedBannerKey)
                      return null;
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          background: "#FEF2F2",
                          border: "1px solid #FECACA",
                          borderRadius: "10px",
                          padding: "12px 14px",
                          margin: "0 0 16px",
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            width: "26px",
                            height: "26px",
                            borderRadius: "50%",
                            background: "#FEE2E2",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#DC2626",
                          }}
                        >
                          <FaClock style={{ fontSize: "13px" }} />
                        </span>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              color: "#B91C1C",
                              fontSize: "13px",
                              fontWeight: 700,
                            }}
                          >
                            {banner.title}
                          </span>
                          <span
                            style={{
                              color: "#7F1D1D",
                              fontSize: "12.5px",
                              lineHeight: 1.45,
                            }}
                          >
                            {banner.detail}
                          </span>
                        </div>
                        <button
                          type="button"
                          aria-label="Dismiss"
                          onClick={() => setDismissedBannerKey(banner.key)}
                          style={{
                            flexShrink: 0,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#B91C1C",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px",
                            lineHeight: 1,
                          }}
                        >
                          <IoClose style={{ fontSize: "16px" }} />
                        </button>
                      </div>
                    );
                  })()}

                  <div className={styles["field-block"]}>
                    <div className={`${styles["label"]} mb-2`}>
                      Patient Name
                    </div>
                    <div className={styles["user-section"]}>
                      <div className={styles["userInfo"]}>
                        {/* Child booking: the CHILD is the patient, so the header shows the
                            child's name + "Child" badge, and the account holder is shown as
                            the Guardian in the "Imaging For: Child" block below — the same
                            split the doctor app's appointment details make. No patient photo
                            or child icon is displayed. */}
                        <h3>
                          {childHeaderName || order?.patient?.name || "-"}
                          {childHeaderName && (
                            <span
                              // Same teal pill the lab app's order details use.
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                background: "#0cbdbd",
                                color: "#fff",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontWeight: 500,
                                verticalAlign: "middle",
                              }}
                            >
                              Child
                            </span>
                          )}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Imaging For: Child — shown only when a parent/guardian booked
                      this order for their child (backend flag `is_for_child`).
                      Purely additive: when false/absent nothing renders and the
                      existing patient/guardian info above is unchanged. Reads
                      snake_case fields from the detail payload with camelCase
                      fallbacks so it works regardless of casing. */}
                  {(order?.is_for_child === true ||
                    order?.isForChild === true) &&
                    (() => {
                      const childName =
                        [
                          order?.child_name ?? order?.childName,
                          order?.child_surname,
                        ]
                          .filter(Boolean)
                          .join(" ") || null;
                      const childDob =
                        order?.child_date_of_birth ?? order?.childDateOfBirth;
                      const childGender =
                        order?.child_gender ?? order?.childGender;
                      const childAge = getWholeYearsAge(childDob);
                      return (
                        <div className={styles["medical-history"]}>
                          <div
                            className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                          >
                            <h2>Imaging For: Child</h2>
                          </div>
                          <div
                            className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                          >
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Child Name</div>
                              <div className={styles["value"]}>
                                {childName || "-"}
                              </div>
                            </div>
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Age</div>
                              <div className={styles["value"]}>
                                {childAge != null ? `${childAge} yr` : "-"}
                              </div>
                            </div>
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Gender</div>
                              <div className={styles["value"]}>
                                {capitalizeWord(childGender) || "-"}
                              </div>
                            </div>
                            {/* The account holder who booked for the child. */}
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                Guardian Name
                              </div>
                              <div className={styles["value"]}>
                                {order?.patient?.name || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  <div className={styles["medical-history"]}>
                    <div
                      className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                    >
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Age | Gender</div>
                        <div className={styles["value"]}>
                          {/* Same person as the header: the child on a child booking. */}
                          {childHeaderName ? (
                            <>
                              {getWholeYearsAge(getBookingChildDob(order)) != null
                                ? `${getWholeYearsAge(getBookingChildDob(order))} yr`
                                : "-"}{" "}
                              | {capitalizeWord(getBookingChildGender(order)) || "-"}
                            </>
                          ) : (
                            <>
                              {order?.patient?.age
                                ? `${order.patient.age} yr`
                                : "-"}{" "}
                              | {order?.patient?.gender || "-"}
                            </>
                          )}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Mobile Number</div>
                        <div className={styles["value"]}>
                          {order?.patient?.mobileNumber || "-"}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Technician Gender
                        </div>
                        <div className={styles["value"]}>
                          {order?.preferredTechnicianGender || "-"}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Service Type</div>
                        <div className={styles["value"]}>
                          <span
                            className={`${styles["db-status"]} ${styles["status-imagingtype"]}`}
                          >
                            Imaging
                          </span>
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Location
                        </div>
                        <div className={styles["value"]}>
                          <span
                            style={{
                              color: isHomeVisitOrder() ? "#2563EB" : "#7C3AED",
                              fontWeight: 500,
                              fontSize: "13px",
                            }}
                          >
                            {isHomeVisitOrder() ? "Home Visit" : "On-Site"}
                          </span>
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Preferred Date</div>
                        <div className={styles["value"]}>
                          {fmt(order?.preferredDate)}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Time Slot
                        </div>
                        <div className={styles["value"]}>
                          {order?.preferredTimeSlot
                            ? (() => {
                                const slot =
                                  order.preferredTimeSlot.toUpperCase();
                                const periodName =
                                  slot === "MORNING"
                                    ? "Morning"
                                    : slot === "AFTERNOON"
                                      ? "Afternoon"
                                      : slot === "EVENING"
                                        ? "Evening"
                                        : `${order.preferredTimeSlot.charAt(0).toUpperCase() + order.preferredTimeSlot.slice(1).toLowerCase()}`;
                                // When the preferred period is outside the center's operating
                                // hours for that date (closed day / period out of hours / already
                                // passed today), show the period with a clear tag instead of a
                                // misleading time range.
                                // Not flagged once the schedule is fixed (invoice sent or
                                // order rejected): a later operating-hours change doesn't matter.
                                const status = evaluatePreferredSlotStatus();
                                if (
                                  !isServiceScheduleFixed &&
                                  status.loaded &&
                                  !status.open
                                ) {
                                  // Reason-specific tag: a fully closed day reads "Center
                                  // closed" (not "outside hours"), an elapsed today slot
                                  // reads "Already passed", else "Outside operating hours".
                                  const tagText =
                                    status.reason === "CLOSED_DAY"
                                      ? "Center closed"
                                      : status.reason === "PAST"
                                        ? "Already passed"
                                        : "Outside operating hours";
                                  return (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <span>{periodName}</span>
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          background: "#FEF2F2",
                                          color: "#B91C1C",
                                          border: "1px solid #FECACA",
                                          borderRadius: "999px",
                                          padding: "1px 8px",
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {tagText}
                                      </span>
                                    </span>
                                  );
                                }
                                // Otherwise: the operating-hours-clipped window on the preferred
                                // date when open, else the generic period band as a fallback.
                                const win = getPreferredSlotWindow();
                                const staticRange =
                                  slot === "MORNING"
                                    ? "8:00 AM - 12:00 PM"
                                    : slot === "AFTERNOON"
                                      ? "12:00 PM - 5:00 PM"
                                      : slot === "EVENING"
                                        ? "5:00 PM - 7:30 PM"
                                        : null;
                                const rangeText = win
                                  ? `${minutesToLabel12(win.startMin)} - ${minutesToLabel12(win.endMin)}`
                                  : staticRange;
                                return rangeText
                                  ? `${periodName} (${rangeText})`
                                  : periodName;
                              })()
                            : "-"}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Payment Method</div>
                        <div className={styles["value"]}>
                          <span className={styles["badge-cod"]}>
                            {order?.paymentMethod || "-"}
                          </span>
                        </div>
                      </div>
                      {/* Insurance Status field hidden by product request —
                          imaging has no insurance flow, so the top-level
                          "Patient Information" row is suppressed to reduce
                          noise on the detail page. */}
                    </div>
                  </div>
                  <div className={styles["medical-history"]}>
                    <div
                      className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                    >
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Age | Gender</div>
                        <div className={styles["value"]}>
                          {/* Same person as the header: the child on a child booking. */}
                          {childHeaderName ? (
                            <>
                              {getWholeYearsAge(getBookingChildDob(order)) != null
                                ? `${getWholeYearsAge(getBookingChildDob(order))} yr`
                                : "-"}{" "}
                              | {capitalizeWord(getBookingChildGender(order)) || "-"}
                            </>
                          ) : (
                            <>
                              {order?.patient?.age
                                ? `${order.patient.age} yr`
                                : "-"}{" "}
                              | {order?.patient?.gender || "-"}
                            </>
                          )}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Mobile Number</div>
                        <div className={styles["value"]}>
                          {order?.patient?.mobileNumber || "-"}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Technician Gender
                        </div>
                        <div className={styles["value"]}>
                          {order?.preferredTechnicianGender || "-"}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Service Type</div>
                        <div className={styles["value"]}>
                          <span
                            className={`${styles["db-status"]} ${styles["status-imagingtype"]}`}
                          >
                            Imaging
                          </span>
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Service Location</div>
                        <div className={styles["value"]}>
                          {(() => {
                            const displayTests =
                              tests.length > 0 ? tests : order?.tests || [];
                            if (displayTests.length === 0) {
                              return (
                                <span
                                  style={{
                                    color: "#F59E0B",
                                    fontWeight: 500,
                                    fontSize: "13px",
                                  }}
                                >
                                  N/A
                                </span>
                              );
                            }
                            const locations = [
                              ...new Set(
                                displayTests.map(
                                  (t) => t.location || "ON_SITE",
                                ),
                              ),
                            ];
                            if (locations.length > 1) {
                              return (
                                <span
                                  style={{
                                    color: "#F59E0B",
                                    fontWeight: 500,
                                    fontSize: "13px",
                                  }}
                                >
                                  Mixed
                                </span>
                              );
                            }
                            const loc = locations[0];
                            return (
                              <span
                                style={{
                                  color:
                                    loc === "HOME_VISIT"
                                      ? "#2563EB"
                                      : "#7C3AED",
                                  fontWeight: 500,
                                  fontSize: "13px",
                                }}
                              >
                                {loc === "HOME_VISIT"
                                  ? "Home Visit"
                                  : "On-Site"}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Service Date
                          <span
                            style={{ color: "#EF4444", marginLeft: 2 }}
                            aria-hidden="true"
                          >
                            *
                          </span>
                        </div>
                        <div className={styles["date-wrapper"]}>
                          <DatePicker
                            className={styles["apt-date-filter"]}
                            selected={serviceDate}
                            onChange={(d) => {
                              dateSelectedByUserRef.current = true;
                              setServiceDate(d);
                              setServiceTimeTipDismissed(false);
                            }}
                            placeholderText="Select Date"
                            dateFormat="dd-MM-yyyy"
                            minDate={minServiceDate}
                            disabled={isServiceDateTimeDisabled}
                          />
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Service Time
                          <span
                            style={{ color: "#EF4444", marginLeft: 2 }}
                            aria-hidden="true"
                          >
                            *
                          </span>
                        </div>
                        <div className={styles["value"]}>
                          <div className={styles["apt-field-wrapper"]}>
                            <RxClock className={styles["apt-field-icon"]} />
                            <select
                              value={serviceTime}
                              onChange={(e) => setServiceTime(e.target.value)}
                              className={styles["apt-field-input"]}
                              disabled={isServiceDateTimeDisabled}
                              style={{
                                appearance: "none",
                                paddingRight: "30px",
                                cursor: isServiceDateTimeDisabled
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              <option value="">
                                {order?.scheduledTime || "Select time"}
                              </option>
                              {timeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <RxChevronDown
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#9CA3AF",
                              }}
                            />
                          </div>
                        </div>
                        {/* Center is open but the preferred period has no bookable slots
                            for this date (outside operating hours, or all slots already
                            passed today) — shown as a dismissible tooltip on an amber chip. */}
                        {(() => {
                          // Not shown once the schedule is fixed (invoice sent or order rejected).
                          if (isServiceScheduleFixed) return null;
                          if (!serviceDate || !order?.preferredTimeSlot)
                            return null;
                          const selStr = formatDateYMD(serviceDate);
                          if (!centerSlots || centerSlots.date !== selStr)
                            return null;
                          if (centerSlots.is_open === false) return null; // handled by the closed badge/toast
                          if (timeOptions.length > 0) return null;
                          if (serviceTimeTipDismissed) return null;
                          const periodName =
                            order.preferredTimeSlot.charAt(0).toUpperCase() +
                            order.preferredTimeSlot.slice(1).toLowerCase();
                          return (
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "flex-start",
                                marginTop: "6px",
                                background: "#FFFBEB",
                                border: "1px solid #FDE68A",
                                borderRadius: "8px",
                                padding: "8px 10px",
                              }}
                            >
                              <FaInfoCircle
                                style={{
                                  color: "#D97706",
                                  marginTop: "2px",
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  color: "#92400E",
                                  fontSize: "12px",
                                  lineHeight: 1.45,
                                  flex: 1,
                                }}
                              >
                                No available time slots for the {periodName}{" "}
                                slot on this date. Please choose another date.
                              </span>
                              <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setServiceTimeTipDismissed(true)}
                                style={{
                                  flexShrink: 0,
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#B45309",
                                  display: "inline-flex",
                                  padding: 0,
                                }}
                              >
                                <IoClose style={{ fontSize: "15px" }} />
                              </button>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Center Availability — reflects the SELECTED service date (updates on change) */}
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Center Availability
                        </div>
                        <div className={styles["value"]}>
                          {(() => {
                            // Same badge shape as Payment Status, with the center-availability colors.
                            const badgeBase = {
                              padding: "6px 16px",
                              borderRadius: "8px",
                              fontSize: "14px",
                              fontWeight: 500,
                            };
                            if (!serviceDate)
                              return (
                                <span
                                  style={{ color: "#6B7280", fontSize: "13px" }}
                                >
                                  Select a service date
                                </span>
                              );
                            // Only show availability that matches the currently selected
                            // service date (avoids a stale badge while the fetch is in flight).
                            const selStr = formatDateYMD(serviceDate);
                            if (!centerSlots || centerSlots.date !== selStr) {
                              return (
                                <span
                                  style={{ color: "#6B7280", fontSize: "13px" }}
                                >
                                  Checking availability…
                                </span>
                              );
                            }
                            if (centerSlots.is_open === false) {
                              // Schedule fixed (invoice sent or order rejected): a later
                              // change to the operating hours is not flagged.
                              if (isServiceScheduleFixed)
                                return (
                                  <span
                                    style={{ color: "#6B7280", fontSize: "13px" }}
                                  >
                                    —
                                  </span>
                                );
                              return (
                                <span
                                  style={{
                                    ...badgeBase,
                                    backgroundColor: "#FEE2E2",
                                    color: "#B91C1C",
                                  }}
                                >
                                  Center closed
                                </span>
                              );
                            }
                            if (centerSlots.is_open === true) {
                              return (
                                <span
                                  style={{
                                    ...badgeBase,
                                    backgroundColor: "#DCFCE7",
                                    color: "#15803D",
                                  }}
                                >
                                  Center open
                                </span>
                              );
                            }
                            return (
                              <span
                                style={{ color: "#6B7280", fontSize: "13px" }}
                              >
                                —
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Payment Method</div>
                        <div className={styles["value"]}>
                          {order?.paymentMethod ? (
                            <span className={styles["badge-cod"]}>
                              {order.paymentMethod}
                            </span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Payment Status</div>
                        <div className={styles["value"]}>
                          {order?.paymentStatus ? (
                            order.paymentStatus === "REFUNDED" ||
                            order.paymentStatus === "PARTIALLY_REFUNDED" ? (
                              <span
                                className={styles["badge-failed"]}
                                style={{
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                }}
                              >
                                {order.paymentStatus === "PARTIALLY_REFUNDED"
                                  ? "Partially Refunded"
                                  : "Refunded"}
                              </span>
                            ) : (
                              <span
                                className={
                                  order.paymentStatus === "COMPLETED"
                                    ? styles["badge-paid"]
                                    : order.paymentStatus === "FAILED"
                                      ? styles["badge-failed"]
                                      : styles["badge-cod"]
                                }
                              >
                                {order.paymentStatus === "COMPLETED"
                                  ? "Payment Complete"
                                  : order.paymentStatus === "PENDING"
                                    ? "Pending"
                                    : order.paymentStatus === "FAILED"
                                      ? "Failed"
                                      : order.paymentStatus}
                              </span>
                            )
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`row mb-3 pb-1 ${styles["details-grid"]}`}>
                  <div className={`col-lg-6 ${styles["details-grid-leftCol"]}`}>
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Case Note</h2>
                      </div>
                      <div className={styles["grid-col-4"]}>
                        <div className={styles["field-block"]}>
                          <div
                            className={`${styles["value"]} ${styles["font-medium"]} ${styles["address-box"]}`}
                          >
                            {order?.caseNotes || "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`${styles["white-card"]} ${styles["address-card"]} mb-0`}
                    >
                      <div className={styles["patient-tab-title"]}>
                        <h2>Patient Address</h2>
                      </div>
                      <div className={styles["grid-col-4"]}>
                        <div
                          className={`${styles["field-block"]} ${styles["address-field"]}`}
                        >
                          <div
                            className={`${styles["value"]} ${styles["font-medium"]} ${styles["address-box"]}`}
                          >
                            {order?.patientAddress ? (
                              <>
                                <LiaMapMarkerSolid />{" "}
                                {order.patientAddress.house_flat_floor_no ||
                                  "N/A"}
                                ,
                                <br />
                                {order.patientAddress.street || "N/A"},
                                <br />
                                {order.patientAddress.area_district || "N/A"},
                                <br />
                                {order.patientAddress.city || "N/A"},{" "}
                                {order.patientAddress.governorate || "N/A"}
                                {order.patientAddress.country && (
                                  <>
                                    <br />
                                    {order.patientAddress.country}
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <LiaMapMarkerSolid /> N/A
                              </>
                            )}
                          </div>
                          {(() => {
                            const addr = order?.patientAddress;
                            const hasCoords =
                              addr &&
                              addr.latitude != null &&
                              addr.latitude !== "" &&
                              addr.longitude != null &&
                              addr.longitude !== "" &&
                              !isNaN(Number(addr.latitude)) &&
                              !isNaN(Number(addr.longitude));
                            const textAddress = addr
                              ? [
                                  addr.house_flat_floor_no,
                                  addr.street,
                                  addr.area_district,
                                  addr.city,
                                  addr.governorate,
                                  addr.country,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : "";
                            const destination = hasCoords
                              ? `${addr.latitude},${addr.longitude}`
                              : textAddress;
                            const mapsUrl = destination
                              ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving&dir_action=navigate`
                              : null;
                            return mapsUrl ? (
                              <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles["maps-link"]}
                              >
                                Open in Maps
                              </a>
                            ) : (
                              <span
                                className={`${styles["maps-link"]} ${styles["maps-link-disabled"]}`}
                              >
                                Open in Maps
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div
                      className={`${styles["white-card"]} ${styles["prescription-card"]}`}
                    >
                      <div className={styles["patient-tab-title"]}>
                        <h2>Prescription</h2>
                      </div>
                      <div>
                        <div className={styles["medical-history"]}>
                          {(() => {
                            const prescriptionDocs = order?.prescriptions || [];

                            if (prescriptionDocs.length === 0) {
                              return (
                                <div className="text-center py-4">
                                  <AiOutlineFilePdf
                                    size={48}
                                    className="text-muted mb-2"
                                  />
                                  <p className="text-muted mb-0">
                                    No prescription found
                                  </p>
                                  <small className="text-muted">
                                    No prescription has been uploaded for this
                                    order
                                  </small>
                                </div>
                              );
                            }

                            return prescriptionDocs.map((doc, index) => {
                              const docUrl =
                                doc.presignedUrl || doc.presigned_url;
                              const fileAvailable =
                                doc.fileAvailable !== false && !!docUrl;
                              return (
                                <div
                                  key={doc.id || index}
                                  className={styles["upload-row"]}
                                >
                                  <div
                                    className={
                                      styles["upload-item"] +
                                      " " +
                                      styles["w-100"]
                                    }
                                  >
                                    <div className={styles["file-name"]}>
                                      <AiOutlineFilePdf
                                        size={30}
                                        className="text-danger"
                                      />
                                      <span className="d-flex flex-column">
                                        <strong>
                                          {doc.fileName ||
                                            "Prescription Document"}
                                        </strong>
                                        <span>
                                          Uploaded:{" "}
                                          {doc.uploadedAt
                                            ? tzCustom(new Date(
                                                doc.uploadedAt,
                                              ), {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                              }) +
                                              " at " +
                                              tzCustom(new Date(
                                                doc.uploadedAt,
                                              ), {
                                                hour: "numeric",
                                                minute: "2-digit",
                                                hour12: true,
                                              })
                                            : "N/A"}
                                        </span>
                                      </span>
                                    </div>
                                    {fileAvailable && (
                                      <a
                                        href={docUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          cursor: "pointer",
                                          textDecoration: "none",
                                          color: "inherit",
                                        }}
                                      >
                                        <RxChevronDown
                                          size={24}
                                          className="text-secondary"
                                        />
                                      </a>
                                    )}
                                  </div>
                                  {fileAvailable ? (
                                    <div className={styles["preview-scroller"]}>
                                      <div
                                        className={styles["uploaded-preview"]}
                                      >
                                        <div
                                          className={styles["preview-wrapper"]}
                                        >
                                          <iframe
                                            src={docUrl}
                                            style={{
                                              width: "100%",
                                              height: "250px",
                                              border: "none",
                                              borderRadius: "8px",
                                            }}
                                            title={`Prescription ${index + 1}`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        border: "1px dashed #FCA5A5",
                                        borderRadius: "8px",
                                        backgroundColor: "#FEF2F2",
                                        padding: "32px 16px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        gap: "6px",
                                        marginTop: "8px",
                                      }}
                                    >
                                      <AiOutlineFilePdf
                                        size={34}
                                        style={{
                                          color: "#B91C1C",
                                          opacity: 0.6,
                                        }}
                                      />
                                      <div
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: "#B91C1C",
                                        }}
                                      >
                                        Prescription not uploaded properly
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: "#6b7280",
                                          maxWidth: 360,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        We couldn't load this prescription. It
                                        may not have been uploaded correctly or
                                        the file is no longer available. Please
                                        ask the patient to re-upload it.
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Medical Document */}
                <div className={styles["white-card"]}>
                  <div className={styles["patient-tab-title"]}>
                    <h2>Medical Document</h2>
                  </div>
                  <div>
                    <div
                      className={styles["medical-history"]}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        padding: "12px 4px",
                      }}
                    >
                      {(() => {
                        const medicalDocs = order?.medicalDocuments || [];

                        if (medicalDocs.length === 0) {
                          return (
                            <div className="p-3 text-secondary fw-semibold">
                              No medical document uploaded
                            </div>
                          );
                        }

                        return medicalDocs.map((doc, index) => {
                          const fileUrl = doc.presignedUrl || doc.fileUrl;
                          return (
                            <div
                              key={doc.id || index}
                              className={styles["upload-row"]}
                            >
                              <a
                                className={
                                  styles["upload-item"] + " " + styles["w-100"]
                                }
                                href={fileUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  cursor: fileUrl ? "pointer" : "default",
                                  textDecoration: "none",
                                  color: "inherit",
                                }}
                              >
                                <div className={styles["file-name"]}>
                                  <AiOutlineFilePdf
                                    size={30}
                                    className="text-danger"
                                  />
                                  <span>
                                    {doc.fileName || "Medical Document"}
                                  </span>
                                </div>
                              </a>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>

                {/* Accept / Reject for a newly-received order live in the
                    "Acceptance required" panel at the top of this tab. The
                    Test Details + Invoice sections stay hidden until accept. */}

                {/* A rejected order is explained by the red "Order rejected"
                    panel (with the reason) at the top of this tab. */}

                {showTestAndInvoiceSections && (
                  <>
                    {/* Test Details */}
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Test Details</h2>
                        {order?.status === "REQUEST_SUBMITTED" &&
                          isImagingAccepted &&
                          !order?.patientConfirmationReceivedAt && (
                            <button
                              onClick={() => {
                                setCurrentTest((p) => ({
                                  ...p,
                                  location: getEffectiveLocation(),
                                }));
                                setShowAddTestRow(true);
                              }}
                              className={styles["add-test-link"]}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <FaCirclePlus /> Add Test
                            </button>
                          )}
                      </div>
                      <div className={`${styles["bg-gray-light"]} p-2`}>
                        <div
                          className={`${styles["apt-appointments-table"]} ${styles["test-details-table"]}`}
                        >
                          <table>
                            <thead>
                              <tr>
                                <th>Test Name</th>
                                <th>Location</th>
                                <th>Price</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tests.length === 0 && !showAddTestRow ? (
                                (order?.tests || []).length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      style={{
                                        textAlign: "center",
                                        padding: "20px",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Please add a test
                                    </td>
                                  </tr>
                                ) : (
                                  order.tests.map((item, idx) => (
                                    <tr key={idx}>
                                      <td>{item.testName}</td>
                                      <td>
                                        <span
                                          className={`${styles["db-status"]} ${styles[getStatusColor(item.location === "HOME_VISIT" ? "Home Visit" : "On-Site")]}`}
                                        >
                                          {item.location === "HOME_VISIT"
                                            ? "Home Visit"
                                            : "On-Site"}
                                        </span>
                                      </td>
                                      <td>
                                        IQD{" "}
                                        {parsePrice(item.price).toLocaleString(
                                          "en-US",
                                          {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                          },
                                        )}
                                      </td>
                                      <td>
                                        {(() => {
                                          const isEditable =
                                            order?.status ===
                                              "REQUEST_SUBMITTED" &&
                                            !order?.patientConfirmationReceivedAt;
                                          const disabledStyle = !isEditable
                                            ? {
                                                opacity: 0.5,
                                                cursor: "not-allowed",
                                              }
                                            : {};
                                          return (
                                            <div
                                              className={
                                                styles["apt-action-buttons"]
                                              }
                                            >
                                              <button
                                                className={
                                                  styles["apt-view-btn"]
                                                }
                                                title="Edit"
                                                disabled={!isEditable}
                                                style={disabledStyle}
                                                onClick={() =>
                                                  isEditable &&
                                                  startEditingTest(item)
                                                }
                                              >
                                                <EditIcon />
                                              </button>
                                              <button
                                                className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                title="Delete"
                                                disabled={!isEditable}
                                                style={disabledStyle}
                                                onClick={() =>
                                                  isEditable &&
                                                  removeTestFromList(item.id)
                                                }
                                              >
                                                <Trash2 size={24} />
                                              </button>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  ))
                                )
                              ) : (
                                <>
                                  {tests
                                    .filter((t) => t && t.testName && t.price)
                                    .map((item, idx) =>
                                      editingTestId === item.id ? (
                                        <tr key={idx}>
                                          <td>
                                            <TestSearchSelect
                                              services={
                                                availableImagingServicesForEdit
                                              }
                                              value={editingTestServiceId}
                                              onSelect={
                                                handleEditServiceSelection
                                              }
                                              loading={isServicesLoading}
                                              placeholder="Search by test name or code"
                                            />
                                          </td>
                                          <td>
                                            <select
                                              value={editingTest.location}
                                              onChange={(e) => {
                                                const newLoc = e.target.value;
                                                const isPreferredOnSite =
                                                  normalizeLocationCode(
                                                    order?.preferredLocation ||
                                                      order?.preferredLocationRaw,
                                                  ) === LOCATION_CODES.ON_SITE;
                                                if (
                                                  isPreferredOnSite &&
                                                  newLoc ===
                                                    LOCATION_CODES.HOME_VISIT
                                                ) {
                                                  toast.warning(
                                                    "Cannot change to Home Visit when preferred location is On-Site",
                                                  );
                                                  return;
                                                }
                                                // A location change applies to every test in the order.
                                                applyLocationToAllTests(newLoc);
                                              }}
                                              className={
                                                styles["apt-status-filter"]
                                              }
                                            >
                                              {allowedLocations.includes(
                                                LOCATION_CODES.HOME_VISIT,
                                              ) && (
                                                <option value="HOME_VISIT">
                                                  Home Visit
                                                </option>
                                              )}
                                              {allowedLocations.includes(
                                                LOCATION_CODES.ON_SITE,
                                              ) && (
                                                <option value="ON_SITE">
                                                  On-site
                                                </option>
                                              )}
                                            </select>
                                          </td>
                                          <td>
                                            <AmountInput
                                              placeholder="IQD 80,000"
                                              value={editingTest.price}
                                              onValueChange={(value) =>
                                                setEditingTest((p) => ({
                                                  ...p,
                                                  price: value,
                                                }))
                                              }
                                              className={
                                                styles["apt-field-input"]
                                              }
                                            />
                                          </td>
                                          <td>
                                            <div
                                              className={
                                                styles["apt-action-buttons"]
                                              }
                                            >
                                              <button
                                                className={
                                                  styles["apt-view-btn"]
                                                }
                                                title="Save"
                                                onClick={saveEditedTest}
                                              >
                                                <GreenCheckSVG />
                                              </button>
                                              <button
                                                className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                title="Cancel"
                                                onClick={cancelEditingTest}
                                              >
                                                <RedXSVG />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ) : (
                                        <tr key={idx}>
                                          <td>{item.testName}</td>
                                          <td>
                                            <span
                                              className={`${styles["db-status"]} ${styles[getStatusColor(item.location === "HOME_VISIT" ? "Home Visit" : "On-Site")]}`}
                                            >
                                              {item.location === "HOME_VISIT"
                                                ? "Home Visit"
                                                : "On-Site"}
                                            </span>
                                          </td>
                                          <td>
                                            IQD{" "}
                                            {parsePrice(
                                              item.price,
                                            ).toLocaleString("en-US", {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            })}
                                          </td>
                                          <td>
                                            {(() => {
                                              const isEditable =
                                                order?.status ===
                                                  "REQUEST_SUBMITTED" &&
                                                !order?.patientConfirmationReceivedAt;
                                              const disabledStyle = !isEditable
                                                ? {
                                                    opacity: 0.5,
                                                    cursor: "not-allowed",
                                                  }
                                                : {};
                                              return (
                                                <div
                                                  className={
                                                    styles["apt-action-buttons"]
                                                  }
                                                >
                                                  <button
                                                    className={
                                                      styles["apt-view-btn"]
                                                    }
                                                    title="Edit"
                                                    disabled={!isEditable}
                                                    style={disabledStyle}
                                                    onClick={() =>
                                                      isEditable &&
                                                      startEditingTest(item)
                                                    }
                                                  >
                                                    <EditIcon />
                                                  </button>
                                                  <button
                                                    className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                                    title="Delete"
                                                    disabled={!isEditable}
                                                    style={disabledStyle}
                                                    onClick={() =>
                                                      isEditable &&
                                                      removeTestFromList(
                                                        item.id,
                                                      )
                                                    }
                                                  >
                                                    <Trash2 size={24} />
                                                  </button>
                                                </div>
                                              );
                                            })()}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  {showAddTestRow && (
                                    <tr>
                                      <td>
                                        <TestSearchSelect
                                          services={availableImagingServices}
                                          value={selectedServiceId}
                                          onSelect={handleServiceSelection}
                                          loading={isServicesLoading}
                                          placeholder="Search by test name or code"
                                        />
                                      </td>
                                      <td>
                                        <select
                                          value={currentTest.location}
                                          onChange={(e) => {
                                            const newLoc = e.target.value;
                                            const isPreferredOnSite =
                                              normalizeLocationCode(
                                                order?.preferredLocation ||
                                                  order?.preferredLocationRaw,
                                              ) === LOCATION_CODES.ON_SITE;
                                            if (
                                              isPreferredOnSite &&
                                              newLoc ===
                                                LOCATION_CODES.HOME_VISIT
                                            ) {
                                              toast.warning(
                                                "Cannot change to Home Visit when preferred location is On-Site",
                                              );
                                              return;
                                            }
                                            // A location change applies to every test in the order.
                                            applyLocationToAllTests(newLoc);
                                          }}
                                          className={
                                            styles["apt-status-filter"]
                                          }
                                          disabled={!selectedServiceId}
                                          style={
                                            !selectedServiceId
                                              ? {
                                                  backgroundColor: "#f3f4f6",
                                                  cursor: "not-allowed",
                                                  opacity: 0.6,
                                                }
                                              : {}
                                          }
                                        >
                                          {allowedLocations.includes(
                                            LOCATION_CODES.HOME_VISIT,
                                          ) && (
                                            <option value="HOME_VISIT">
                                              Home Visit
                                            </option>
                                          )}
                                          {allowedLocations.includes(
                                            LOCATION_CODES.ON_SITE,
                                          ) && (
                                            <option value="ON_SITE">
                                              On-site
                                            </option>
                                          )}
                                        </select>
                                      </td>
                                      <td>
                                        <AmountInput
                                          placeholder="IQD 80,000"
                                          value={currentTest.price}
                                          onValueChange={(value) =>
                                            setCurrentTest((p) => ({
                                              ...p,
                                              price: value,
                                            }))
                                          }
                                          className={styles["apt-field-input"]}
                                          disabled={!selectedServiceId}
                                          style={
                                            !selectedServiceId
                                              ? {
                                                  backgroundColor: "#f3f4f6",
                                                  cursor: "not-allowed",
                                                  opacity: 0.6,
                                                }
                                              : {}
                                          }
                                        />
                                      </td>
                                      <td>
                                        <div
                                          className={
                                            styles["apt-action-buttons"]
                                          }
                                        >
                                          <button
                                            className={styles["apt-view-btn"]}
                                            title="Add Test"
                                            onClick={addTestToList}
                                            disabled={!selectedServiceId}
                                            style={
                                              !selectedServiceId
                                                ? {
                                                    opacity: 0.4,
                                                    cursor: "not-allowed",
                                                  }
                                                : {}
                                            }
                                          >
                                            <GreenCheckSVG />
                                          </button>
                                          <button
                                            className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`}
                                            title="Cancel"
                                            onClick={() => {
                                              setShowAddTestRow(false);
                                              setSelectedServiceId("");
                                              setCurrentTest({
                                                testName: "",
                                                serviceCode: "",
                                                price: "",
                                                transportationFee: "0",
                                                location: "ON_SITE",
                                              });
                                            }}
                                          >
                                            <RedXSVG />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Transportation Fee - only for Home Visit tests */}
                    {(() => {
                      const displayTests =
                        tests.length > 0 ? tests : order?.tests || [];
                      const hasHomeVisitTests = displayTests.some(
                        (t) => t.location === "HOME_VISIT",
                      );
                      if (
                        !hasHomeVisitTests ||
                        order?.status !== "REQUEST_SUBMITTED"
                      )
                        return null;
                      return (
                        <>
                          {!transportationFeeAdded && (
                            <div style={{ padding: "12px 20px" }}>
                              <button
                                onClick={() => {
                                  setTempTransportationFee(
                                    orderTransportationFee === "0"
                                      ? ""
                                      : orderTransportationFee,
                                  );
                                  setShowTransportationFeePopup(true);
                                }}
                                style={{
                                  background: "none",
                                  border: "2px dashed #099494",
                                  borderRadius: "8px",
                                  padding: "10px 20px",
                                  color: "#099494",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                + Add Transportation Fee
                              </button>
                            </div>
                          )}
                          {/* Transportation Fee Popup */}
                          {showTransportationFeePopup && (
                            <div className={dialogStyles["labDialog_wrapper"]}>
                              <div
                                className={dialogStyles["dialog-overlay"]}
                                style={{ alignItems: "center" }}
                              >
                                <div
                                  className={dialogStyles["dialog-box"]}
                                  style={{ width: "460px" }}
                                >
                                  <button
                                    className={dialogStyles["dialog-close"]}
                                    onClick={() =>
                                      setShowTransportationFeePopup(false)
                                    }
                                  >
                                    ×
                                  </button>
                                  <h2 className={dialogStyles["dialog-title"]}>
                                    {transportationFeeAdded
                                      ? "Edit Transportation Fee"
                                      : "Add Transportation Fee"}
                                  </h2>
                                  <label
                                    style={{
                                      fontSize: "14px",
                                      color: "#374151",
                                      fontWeight: 500,
                                      marginBottom: "6px",
                                      display: "block",
                                    }}
                                  >
                                    Transportation Fee (IQD)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    placeholder="Enter transportation fee"
                                    value={tempTransportationFee}
                                    onChange={(e) =>
                                      setTempTransportationFee(e.target.value)
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "10px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "12px",
                                      fontSize: "14px",
                                      outline: "none",
                                      marginBottom: "4px",
                                      boxSizing: "border-box",
                                    }}
                                    autoFocus
                                  />
                                  <div
                                    className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
                                  >
                                    <button
                                      className={dialogStyles["btn-outlined-1"]}
                                      onClick={() =>
                                        setShowTransportationFeePopup(false)
                                      }
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className={dialogStyles["btn-1"]}
                                      onClick={() => {
                                        // 0 IQD is a valid fee (free transport). Only an
                                        // empty field is refused.
                                        const raw = String(
                                          tempTransportationFee ?? "",
                                        ).trim();
                                        const fee = Number(raw);
                                        if (
                                          raw === "" ||
                                          !Number.isFinite(fee) ||
                                          fee < 0
                                        ) {
                                          toast.error(
                                            "Enter a transportation fee (0 IQD is allowed).",
                                          );
                                          return;
                                        }
                                        explicitZeroFeeRef.current = fee === 0;
                                        setOrderTransportationFee(String(fee));
                                        setTransportationFeeAdded(true);
                                        setShowTransportationFeePopup(false);
                                      }}
                                      style={{
                                        justifyContent: "center",
                                        border: "1px solid transparent",
                                        boxSizing: "border-box",
                                      }}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Invoice */}
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Invoice</h2>
                        {tests.length > 0 && (
                          <span style={{ fontSize: "13px", color: "#666" }}>
                            {tests.length} test(s) added
                          </span>
                        )}
                      </div>
                      {tests.length === 0 &&
                      (order?.tests || []).length === 0 ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#999",
                          }}
                        >
                          No tests added yet. Add tests using the form above.
                        </div>
                      ) : (
                        (() => {
                          // Use local tests if being edited, otherwise use saved order tests
                          const displayTests =
                            tests.length > 0 ? tests : order?.tests || [];
                          const subtotal = displayTests.reduce(
                            (s, t) => s + parsePrice(t.price),
                            0,
                          );
                          const hasHomeVisitTests =
                            isHomeVisitOrder() ||
                            displayTests.some(
                              (t) => t.location === "HOME_VISIT",
                            );
                          const transportationFee = hasHomeVisitTests
                            ? parseFloat(orderTransportationFee) || 0
                            : 0;
                          // Admin fee is calculated on (subtotal + transportation fee), matching the
                          // backend invoice and the lab module — not on the subtotal alone.
                          //
                          // Once the booking is paid (or a COD order accepted) it carries the admin
                          // fee actually CHARGED (`pricing.adminFee`); print that, exactly like
                          // ImagingInvoiceModal, so the derived Total below equals what was paid
                          // even if the live percentage changed since.
                          const isPaidStage = [
                            "ORDER_ACCEPTED",
                            "TECHNICIAN_ASSIGNED",
                            "TECHNICIAN_DISPATCHED",
                            "IN_PROGRESS",
                            "COMPLETED",
                          ].includes(String(order?.status || "").toUpperCase());
                          const useFeeSnapshot =
                            isPaidStage && order?.pricing?.adminFee != null;
                          const shownAdminFeePct = useFeeSnapshot
                            ? Number(order?.pricing?.adminFeePercentage) ||
                              adminFeePercentage ||
                              0
                            : adminFeePercentage || 0;
                          const adminFee = useFeeSnapshot
                            ? Math.round(parsePrice(order.pricing.adminFee))
                            : Math.round(
                                (subtotal + transportationFee) *
                                  (adminFeePercentage / 100),
                              );
                          // Loyalty / promotion discount the patient redeemed. Persisted on
                          // the booking (`imaging_bookings.loyalty_discount`) and surfaced on
                          // `pricing` by the order-details endpoint — authoritative for every
                          // payment method (incl. COD). Shown as a deduction so this breakdown's
                          // Total matches the printed invoice and what the patient actually paid.
                          const loyaltyDiscount = parsePrice(
                            order?.pricing?.loyaltyDiscount ??
                              order?.loyaltyDiscount ??
                              0,
                          );
                          const loyaltyPointsUsed = Number(
                            order?.pricing?.loyaltyPointsUsed ??
                              order?.loyaltyPointsUsed ??
                              0,
                          );
                          // Promo code discount and (INSURANCE-method payments only) the
                          // insurance deduction applied at checkout, from the gateway's
                          // order-details enrichment. Each gets its own row below.
                          const promoDiscount = parsePrice(
                            order?.pricing?.promoDiscount || 0,
                          );
                          const promoCode = order?.pricing?.promoCode || null;
                          const insuranceCoverage = parsePrice(
                            order?.pricing?.insuranceCoverage || 0,
                          );
                          // Total DERIVED from the rows shown (as on ImagingInvoiceModal and
                          // the doctor invoice), not the payment's `amountPaid`, which is net
                          // of a promo / insurance deduction that had no row.
                          const total = Math.max(
                            0,
                            Math.round(subtotal + transportationFee + adminFee) -
                              insuranceCoverage -
                              loyaltyDiscount -
                              promoDiscount,
                          );
                          return (
                            <>
                              {displayTests.map((t, i) => (
                                <div key={i} className={styles["test-row"]}>
                                  <div>
                                    <span className={styles["test-label"]}>
                                      {t.testName}
                                    </span>
                                    <span
                                      style={{
                                        marginLeft: "8px",
                                        fontSize: "12px",
                                        color:
                                          t.location === "HOME_VISIT"
                                            ? "#2563EB"
                                            : "#7C3AED",
                                        backgroundColor:
                                          t.location === "HOME_VISIT"
                                            ? "#DBEAFE"
                                            : "#F3E8FF",
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {t.location === "HOME_VISIT"
                                        ? "Home Visit"
                                        : "On-Site"}
                                    </span>
                                    {t.serviceCode && (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#999",
                                          marginTop: "4px",
                                        }}
                                      >
                                        Code: {t.serviceCode}
                                      </div>
                                    )}
                                  </div>
                                  <span className={styles["test-value-strong"]}>
                                    IQD{" "}
                                    {parsePrice(t.price).toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      },
                                    )}
                                  </span>
                                </div>
                              ))}
                              <hr
                                style={{
                                  margin: "16px 0",
                                  border: "none",
                                  borderTop: "1px solid #eee",
                                }}
                              />
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  Subtotal
                                </span>
                                <span className={styles["test-value-strong"]}>
                                  IQD{" "}
                                  {subtotal.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              {/* Shown for an added 0 IQD fee too, so it can still be edited or
                                  removed, and for a home visit once the invoice has been sent
                                  (a 0 fee reads "Free"). */}
                              {(transportationFee > 0 ||
                                (hasHomeVisitTests &&
                                  (transportationFeeAdded ||
                                    order?.status !== "REQUEST_SUBMITTED"))) && (
                                <div className={styles["test-row"]}>
                                  <span
                                    className={styles["test-label"]}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    Transportation Fee
                                    {order?.status === "REQUEST_SUBMITTED" && (
                                      <>
                                        <button
                                          onClick={() => {
                                            setTempTransportationFee(
                                              orderTransportationFee,
                                            );
                                            setShowTransportationFeePopup(true);
                                          }}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "2px",
                                            color: "#099494",
                                          }}
                                          title="Edit"
                                        >
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => {
                                            explicitZeroFeeRef.current = false;
                                            setOrderTransportationFee("0");
                                            setTransportationFeeAdded(false);
                                          }}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "2px",
                                            color: "#EF4444",
                                          }}
                                          title="Remove"
                                        >
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>
                                      </>
                                    )}
                                  </span>
                                  <span className={styles["test-value-strong"]}>
                                    {Number(transportationFee) > 0
                                      ? `IQD ${Number(transportationFee).toLocaleString("en-US", {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        })}`
                                      : t("imagingAppointmentDetail.free", "Free")}
                                  </span>
                                </div>
                              )}
                              {/* Admin fee row only when one actually applies — no fee
                                  configured in the admin panel means no row (and no charge). */}
                              {Number(adminFee) > 0 && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>
                                    Admin Fee (
                                    {formatPercent(shownAdminFeePct)}
                                    %)
                                  </span>
                                  <span className={styles["test-value-strong"]}>
                                    IQD{" "}
                                    {adminFee.toLocaleString("en-US", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </span>
                                </div>
                              )}
                              {insuranceCoverage > 0 && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>
                                    Insurance Coverage
                                  </span>
                                  <span
                                    className={styles["test-value-strong"]}
                                    style={{ color: "#00BC7D" }}
                                  >
                                    − IQD{" "}
                                    {insuranceCoverage.toLocaleString("en-US", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </span>
                                </div>
                              )}
                              {/* Loyalty points redeemed — deduction (green, like the printed
                                  invoice) so the Total reconciles with what was charged. */}
                              {loyaltyDiscount > 0 && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>
                                    Loyalty Point Discount
                                    {loyaltyPointsUsed > 0
                                      ? ` (${loyaltyPointsUsed} pts)`
                                      : ""}
                                  </span>
                                  <span
                                    className={styles["test-value-strong"]}
                                    style={{ color: "#00BC7D" }}
                                  >
                                    − IQD{" "}
                                    {loyaltyDiscount.toLocaleString("en-US", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </span>
                                </div>
                              )}
                              {promoDiscount > 0 && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>
                                    Promo Discount
                                    {promoCode ? ` (${promoCode})` : ""}
                                  </span>
                                  <span
                                    className={styles["test-value-strong"]}
                                    style={{ color: "#00BC7D" }}
                                  >
                                    − IQD{" "}
                                    {promoDiscount.toLocaleString("en-US", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </span>
                                </div>
                              )}
                              <div
                                className={`${styles["test-row"]} ${styles["total-price-row"]}`}
                              >
                                <span className={styles["test-value-strong"]}>
                                  Total Price (Patient Pays)
                                </span>
                                <span className={styles["test-value-strong"]}>
                                  IQD{" "}
                                  {total.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>

                    <div
                      className={`${styles["text-center"]} d-flex flex-wrap justify-content-center gap-2`}
                    >
                      {(() => {
                        const displayTests =
                          tests.length > 0 ? tests : order?.tests || [];
                        const hasHV = displayTests.some(
                          (t) => t.location === "HOME_VISIT",
                        );
                        const needsTransportationFee = hasHV && !transportationFeeAdded;
                        const noTests = displayTests.length === 0;
                        const invoiceAlreadySent =
                          order?.status &&
                          !["REQUEST_SUBMITTED"].includes(order.status);
                        const awaitingSplit =
                          order?.status === "AWAITING_CONFIRMATION";
                        const isDisabled =
                          isSavingTests ||
                          noTests ||
                          needsTransportationFee ||
                          invoiceAlreadySent;

                        // Contextual status message shown once the invoice has been sent.
                        // (INVOICE_RECEIVED / AWAITING_PAYMENT have no message here: the
                        // blue "Invoice sent" panel at the top of the tab covers them.)
                        const statusActionLabel = (() => {
                          switch (order?.status) {
                            case "ORDER_ACCEPTED":
                              return "Payment received. Please assign a technician to proceed with the order.";
                            case "SCHEDULED":
                            case "TECHNICIAN_ASSIGNED":
                              return "A technician has been assigned and the visit is scheduled for the patient.";
                            case "TECHNICIAN_DISPATCHED":
                              return "The technician has been dispatched and is on the way to the patient.";
                            case "IN_PROGRESS":
                              return "The technician is collecting the samples from the patient.";
                            case "COMPLETED": {
                              // A pasted result link counts as a result, like an uploaded PDF.
                              const hasUploadedResults =
                                (order?.testResults || []).length > 0 ||
                                !!order?.resultLink;
                              return hasUploadedResults
                                ? "The order has been completed and the results have been generated."
                                : "The order has been completed. Please upload the results or add a result link in the Results & Reports section to share them with the patient.";
                            }
                            case "REJECTED": {
                              // Append the reason the officer gave, when set.
                              const reason =
                                order?.rejectionReason ||
                                order?.rejection_reason;
                              return reason
                                ? `This order has been rejected. ${reason}`
                                : "This order has been rejected.";
                            }
                            default:
                              return "The invoice has been sent to the patient.";
                          }
                        })();

                        // Green for finished/positive states, red for rejected, amber otherwise.
                        const statusActionColor = (() => {
                          switch (order?.status) {
                            case "ORDER_ACCEPTED":
                            case "COMPLETED":
                              return "#16a34a"; // green
                            case "REJECTED":
                              return "#dc2626"; // red
                            default:
                              return "#D08700"; // amber
                          }
                        })();

                        const statusBoxStyles = (() => {
                          switch (statusActionColor) {
                            case "#16a34a":
                              return {
                                backgroundColor: "#e8f5e8",
                                border: "1px solid #4caf50",
                                color: "#2e7d32",
                              };
                            case "#dc2626":
                              return {
                                backgroundColor: "#fdecea",
                                border: "1px solid #ef5350",
                                color: "#c62828",
                              };
                            default:
                              return {
                                backgroundColor: "#fff8e1",
                                border: "1px solid #f0b429",
                                color: "#D08700",
                              };
                          }
                        })();

                        if (invoiceAlreadySent) {
                          // Invoice sent, waiting for payment: explained by the blue
                          // "Invoice sent" panel at the top of the Order Details tab.
                          if (isAwaitingPatientPayment) {
                            return null;
                          }
                          // For ORDER_ACCEPTED, mirror the lab app: show an action button that
                          // opens the Process tab (on-site) / Technician Detail tab (home visit)
                          // instead of a static "assign a technician" banner. On-site orders are
                          // not assigned a technician — they are processed directly.
                          if (order?.status === "ORDER_ACCEPTED") {
                            const onsite = isOnSiteOrder();
                            return (
                              <button
                                className={styles["apt-register-btn"]}
                                onClick={() =>
                                  setActiveTab("Technician Detail")
                                }
                                style={{
                                  backgroundColor: "#077272",
                                  color: "white",
                                }}
                              >
                                <FaLocationArrow />{" "}
                                {onsite
                                  ? "Start Processing"
                                  : "Assign Technician"}
                              </button>
                            );
                          }
                          return (
                            <div
                              style={{
                                width: "100%",
                                ...statusBoxStyles,
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "14px",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              <Check
                                size={16}
                                style={{ marginRight: "8px", flexShrink: 0 }}
                              />
                              {awaitingSplit
                                ? "Awaiting Patient Confirmation"
                                : statusActionLabel}
                            </div>
                          );
                        }

                        return (
                          <>
                            {needsTransportationFee &&
                              !noTests &&
                              !invoiceAlreadySent && (
                                <div
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    color: "#F59E0B",
                                  }}
                                >
                                  Please add a transportation fee for home visit tests
                                  before sending the invoice.
                                </div>
                              )}
                            {awaitingSplit && (
                              <div
                                style={{
                                  width: "100%",
                                  textAlign: "center",
                                  marginBottom: "8px",
                                  fontSize: "13px",
                                  color: "#F59E0B",
                                }}
                              >
                                Waiting for patient to confirm split decision.
                              </div>
                            )}
                            <button
                              className={styles["apt-register-btn"]}
                              onClick={handleGenerateInvoice}
                              disabled={isDisabled}
                              style={{ opacity: isDisabled ? 0.5 : 1 }}
                            >
                              <FaLocationArrow />{" "}
                              {awaitingSplit
                                ? "Awaiting Patient Confirmation"
                                : invoiceAlreadySent
                                  ? "Invoice Sent"
                                  : "Send Invoice to Patient"}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "Technician Detail" && (
              <div className={`${styles["patient-tab-content"]} p-0 bg-white`}>
                <div className={styles["white-card"]}>
                  <div
                    className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                  >
                    <h2>
                      {isOnSiteOrder() ? "Process" : "Schedule & Dispatch"}
                    </h2>
                  </div>

                  <div className={styles["medical-history"]}>
                    {(() => {
                      const isOnSite = isOnSiteOrder();
                      const isCOD = order?.paymentMethod === "COD";
                      const isSampleCollected =
                        order?.workflowStatus?.sampleCollected ||
                        !!order?.sampleCollectedAt ||
                        order?.status === "COMPLETED";
                      const isJobDone = order?.status === "COMPLETED";

                      // Home-visit technician assignment state, shared by the
                      // dispatch dropdown hint, the status banner and the
                      // WhatsApp button.
                      const isTechnicianAssigned = !!order?.assignedTechnicianId;
                      const canAssignTechnician =
                        order?.status === "ORDER_ACCEPTED" ||
                        order?.status === "TECHNICIAN_ASSIGNED";
                      const showAddTechnicianHint =
                        canAssignTechnician &&
                        !isTechnicianAssigned &&
                        !loadingTechnicians &&
                        !selectedTechnicianId;
                      const isSendWhatsAppDisabled =
                        loadingTechnicians ||
                        assigningTechnician ||
                        !canAssignTechnician ||
                        isTechnicianAssigned ||
                        !selectedTechnicianId;

                      // Technician Status banner. Terminal statuses win; otherwise
                      // an assigned technician counts as "assigned" even after the
                      // order moves on (SCHEDULED / IN_PROGRESS).
                      const technicianStatus =
                        order?.status === "COMPLETED"
                          ? {
                              isPending: false,
                              label: t(
                                "imagingAppointmentDetail.technicianStatusCompleted",
                                "Completed",
                              ),
                            }
                          : order?.status === "TECHNICIAN_DISPATCHED"
                            ? {
                                isPending: false,
                                label: t(
                                  "imagingAppointmentDetail.technicianStatusDispatched",
                                  "Dispatched",
                                ),
                              }
                            : isTechnicianAssigned ||
                                order?.status === "TECHNICIAN_ASSIGNED"
                              ? {
                                  isPending: false,
                                  label: t(
                                    "imagingAppointmentDetail.technicianStatusAssigned",
                                    "Technician Assigned",
                                  ),
                                }
                              : {
                                  isPending: true,
                                  label: t(
                                    "imagingAppointmentDetail.technicianStatusNotAssigned",
                                    "Not Assigned",
                                  ),
                                };

                      return (
                        <>
                          <div
                            className={`${styles["dispatch-grid-3"]} border-0`}
                          >
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                Service Date
                              </div>
                              <div className={styles["value"]}>
                                {order?.scheduledDate
                                  ? tzDateNumeric(new Date(
                                      order.scheduledDate,
                                    ))
                                  : "Not scheduled"}
                              </div>
                            </div>
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>
                                Service Time
                              </div>
                              <div className={styles["value"]}>
                                {formatTime12h(order?.scheduledTime) ||
                                  "Not scheduled"}
                              </div>
                            </div>

                            {/* Show technician fields for HOME_VISIT */}
                            {!isOnSite && (
                              <>
                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    Technician Gender
                                  </div>
                                  <div className={styles["gender-options"]}>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "male"}
                                        onChange={() => setGender("male")}
                                        disabled={
                                          isJobDone ||
                                          !!order?.assignedTechnicianId
                                        }
                                      />
                                      <IoIosCheckbox
                                        className={styles["checkbox-icon"]}
                                        size={24}
                                      />
                                      Male
                                    </label>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "female"}
                                        onChange={() => setGender("female")}
                                        disabled={
                                          isJobDone ||
                                          !!order?.assignedTechnicianId
                                        }
                                      />
                                      <IoIosCheckbox
                                        className={styles["checkbox-icon"]}
                                        size={24}
                                      />
                                      Female
                                    </label>
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    Preferred Technician Gender
                                  </div>
                                  <div className={styles["value"]}>
                                    {order?.preferredTechnicianGender || "Any"}
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    Assigned Technician
                                  </div>
                                  <div className={styles["value"]}>
                                    {order?.assignedTechnician
                                      ? `${order.assignedTechnician.name} (${order.assignedTechnician.serviceCode})`
                                      : "Not assigned"}
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>
                                    Assign Technician
                                  </div>
                                  <div
                                    className={
                                      styles["dispatch-select-wrapper"]
                                    }
                                  >
                                    <select
                                      className={styles["dispatch-select"]}
                                      value={selectedTechnicianId}
                                      onChange={(e) =>
                                        setSelectedTechnicianId(e.target.value)
                                      }
                                      aria-describedby={
                                        showAddTechnicianHint
                                          ? "assign-technician-hint"
                                          : undefined
                                      }
                                      disabled={
                                        loadingTechnicians ||
                                        (order?.status !== "ORDER_ACCEPTED" &&
                                          order?.status !==
                                            "TECHNICIAN_ASSIGNED") ||
                                        !gender ||
                                        order?.assignedTechnicianId
                                      }
                                    >
                                      <option value="">
                                        {loadingTechnicians
                                          ? "Loading..."
                                          : order?.assignedTechnicianId
                                            ? "Technician assigned"
                                            : !gender
                                              ? "Select gender first"
                                              : "Select technician"}
                                      </option>
                                      {availableTechnicians
                                        .filter(
                                          (tech) =>
                                            !gender ||
                                            tech.gender?.toLowerCase() ===
                                              gender.toLowerCase(),
                                        )
                                        .map((tech) => (
                                          <option key={tech.id} value={tech.id}>
                                            {tech.name} ({tech.serviceCode}) -{" "}
                                            {tech.gender}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                  {showAddTechnicianHint && (
                                    <div
                                      id="assign-technician-hint"
                                      className={styles["field-error-text"]}
                                    >
                                      {t(
                                        "imagingAppointmentDetail.addTechnicianHint",
                                        "Please add a technician.",
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          <hr />

                          {isOnSite ? (
                            <div>
                              <div
                                className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}
                              >
                                <h2>Order Actions</h2>
                              </div>

                              <button
                                className={`${styles["apt-register-btn"]} ${isSampleCollected ? styles["gray_colored"] : ""} py-2 px-3 mb-2 me-3`}
                                onClick={async () => {
                                  if (isSampleCollected || isSampleCollecting)
                                    return;
                                  setIsSampleCollecting(true);
                                  try {
                                    const token =
                                      sessionStorage.getItem("usr_1a2b3c");
                                    const res = await axios.post(
                                      `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/onsite-workflow`,
                                      { action: "collect_sample" },
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          "Content-Type": "application/json",
                                        },
                                      },
                                    );
                                    if (res.data.status) {
                                      toast.success(
                                        "Sample marked as collected.",
                                      );
                                      dispatch(fetchImagingOrderDetails(id));
                                    } else {
                                      toast.error(
                                        res.data.message || "Failed to update.",
                                      );
                                    }
                                  } catch (e) {
                                    toast.error("Failed to update.");
                                  } finally {
                                    setIsSampleCollecting(false);
                                  }
                                }}
                                disabled={
                                  isSampleCollected ||
                                  isJobDone ||
                                  isSampleCollecting
                                }
                              >
                                {isSampleCollecting ? (
                                  <span className={styles["btn-spinner"]} />
                                ) : (
                                  <Check size={16} />
                                )}{" "}
                                {isSampleCollected
                                  ? "Sample Collected ✓"
                                  : "Sample Collected"}
                              </button>

                              <button
                                className={`${styles["apt-register-btn"]} ${isJobDone ? styles["gray_colored"] : ""} py-2 px-3 mb-2`}
                                onClick={async () => {
                                  if (isJobDone || isCompletingJob) return;
                                  const action = isCOD
                                    ? "complete_job_cod"
                                    : "complete_job";
                                  setIsCompletingJob(true);
                                  try {
                                    const token =
                                      sessionStorage.getItem("usr_1a2b3c");
                                    const res = await axios.post(
                                      `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${id}/onsite-workflow`,
                                      { action },
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          "Content-Type": "application/json",
                                        },
                                      },
                                    );
                                    if (res.data.status) {
                                      toast.success(
                                        isCOD
                                          ? "Cash collected and job done."
                                          : "Job completed.",
                                      );
                                      dispatch(fetchImagingOrderDetails(id));
                                    } else {
                                      toast.error(
                                        res.data.message || "Failed to update.",
                                      );
                                    }
                                  } catch (e) {
                                    toast.error("Failed to update.");
                                  } finally {
                                    setIsCompletingJob(false);
                                  }
                                }}
                                disabled={
                                  !isSampleCollected ||
                                  isJobDone ||
                                  isCompletingJob
                                }
                              >
                                {isCompletingJob ? (
                                  <span className={styles["btn-spinner"]} />
                                ) : (
                                  <Check size={16} />
                                )}{" "}
                                {isJobDone
                                  ? isCOD
                                    ? "Cash Collected & Job Done ✓"
                                    : "Job Completed ✓"
                                  : isCOD
                                    ? "Mark as Collected / Job Done"
                                    : "Mark as Completed / Job Done"}
                              </button>
                            </div>
                          ) : (
                            <>
                              <div
                                className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}
                              >
                                <h2>Technician Assignment</h2>
                              </div>

                              {/* Status is informational, so it is a banner, not a button. */}
                              <div
                                role="status"
                                className={`${styles["tech-status-banner"]} ${
                                  technicianStatus.isPending
                                    ? styles["tech-status-banner-pending"]
                                    : styles["tech-status-banner-success"]
                                }`}
                              >
                                {technicianStatus.isPending ? (
                                  <FaInfoCircle size={16} aria-hidden="true" />
                                ) : (
                                  <Check size={16} aria-hidden="true" />
                                )}
                                <div>
                                  <strong>{technicianStatus.label}</strong>
                                  {!technicianStatus.isPending &&
                                    order?.assignedTechnician?.name && (
                                      <span
                                        className={
                                          styles["tech-status-banner-detail"]
                                        }
                                      >
                                        {order.assignedTechnician.name}
                                        {order.assignedTechnician.mobileNumber
                                          ? ` (${order.assignedTechnician.mobileNumber})`
                                          : ""}
                                      </span>
                                    )}
                                </div>
                              </div>

                              <div className={`${styles["text-end"]} mt-4 mb-3`}>
                                <button
                                  className={styles["green-btn"]}
                                  onClick={handleAssignTechnician}
                                  // Disabled until a technician is picked; the red
                                  // "Please add a technician." hint under the dropdown
                                  // tells the officer why.
                                  disabled={isSendWhatsAppDisabled}
                                >
                                  {assigningTechnician ? (
                                    <>
                                      <span className={styles["btn-spinner"]} />{" "}
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <FaLocationArrow />{" "}
                                      {order?.assignedTechnicianId
                                        ? "Technician Already Assigned"
                                        : "Send Patient Details via WhatsApp"}
                                    </>
                                  )}
                                </button>
                              </div>

                              {order?.assignedTechnicianId &&
                                order?.assignedTechnician && (
                                  <>
                                    <hr />
                                    <div
                                      style={{
                                        padding: "8px 0",
                                        color: "#6b7280",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Workflow actions are managed by the
                                      technician via the public appointment
                                      link.
                                    </div>
                                  </>
                                )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "Results & Reports" && (
              <>
                <div className={`${styles["patient-tab-content"]} p-0`}>
                  <div
                    className={`${styles["white-card"]} m-0 position-relative`}
                  >
                    {/* Check if report is already sent */}
                    {order?.reportStatus === "SENT" && (
                      <div
                        className={styles["report-sent-notice"]}
                        style={{
                          backgroundColor: "#e8f5e8",
                          border: "1px solid #4caf50",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          marginBottom: "16px",
                          color: "#2e7d32",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <Check
                          size={16}
                          style={{
                            marginRight: "8px",
                            verticalAlign: "middle",
                          }}
                        />
                        Report has been sent to patient. Upload, links, sharing,
                        and file deletion are disabled.
                      </div>
                    )}

                    <input
                      type="file"
                      id="file-upload"
                      className={styles["file-input"]}
                      accept="application/pdf"
                      onChange={handleMultipleFileUpload}
                      disabled={uploadingFile || order?.reportStatus === "SENT"}
                    />
                    {/* Two ways to deliver results: upload the PDF report, or
                        paste a link to the results (e.g. an online image viewer). */}
                    <div
                      className={`${styles["result-upload-card"]} ${uploadingFile ? styles["uploading"] : ""} ${order?.reportStatus === "SENT" ? styles["disabled"] : ""}`}
                      style={{
                        opacity: order?.reportStatus === "SENT" ? 0.6 : 1,
                      }}
                    >
                      <h2>Add Test Results</h2>
                      <p className={styles["result-subtitle"]}>
                        {order?.reportStatus === "SENT"
                          ? "Report has been sent to patient. Upload and adding a link are disabled."
                          : "Upload the final PDF report or add a link to the results. This will be automatically available in the patient's app."}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <label
                          className={styles["result-upload-btn"]}
                          htmlFor="file-upload"
                          style={{
                            cursor:
                              order?.reportStatus === "SENT"
                                ? "not-allowed"
                                : "pointer",
                            pointerEvents:
                              order?.reportStatus === "SENT" || uploadingFile
                                ? "none"
                                : "auto",
                          }}
                        >
                          <Upload size={20} />{" "}
                          {order?.reportStatus === "SENT"
                            ? "Upload Disabled"
                            : uploadingFile
                              ? "Uploading..."
                              : "Upload"}
                        </label>
                        <button
                          type="button"
                          className={styles["result-upload-btn"]}
                          onClick={openResultLinkModal}
                          disabled={
                            order?.reportStatus === "SENT" || savingResultLink
                          }
                          style={{
                            background: "#ffffff",
                            border: "1px solid #0dd0d0",
                            cursor:
                              order?.reportStatus === "SENT"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <Link2 size={20} />{" "}
                          {order?.reportStatus === "SENT"
                            ? "Add Link Disabled"
                            : order?.resultLink
                              ? "Replace Link"
                              : "Add Link"}
                        </button>
                      </div>
                    </div>

                    <div className={`${styles["result-file-list"]} mt-4`}>
                      {order?.resultLink && (
                        <div className={styles["result-file-item"]}>
                          <span className={styles["result-file-icon"]}>
                            <Link2 size={20} color="#155DFC" />
                          </span>
                          {isValidResultLink(order.resultLink) ? (
                            <a
                              href={order.resultLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={order.resultLink}
                              className={styles["result-file-name"]}
                              style={{
                                color: "#155DFC",
                                textDecoration: "underline",
                                display: "inline-block",
                                maxWidth: "360px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              {order.resultLink}
                            </a>
                          ) : (
                            <span className={styles["result-file-name"]}>
                              {order.resultLink}
                            </span>
                          )}
                          <button
                            className={styles["result-delete-btn"]}
                            onClick={handleRemoveResultLink}
                            disabled={order?.reportStatus === "SENT"}
                            title="Remove link"
                            style={{
                              opacity: order?.reportStatus === "SENT" ? 0.4 : 1,
                              cursor:
                                order?.reportStatus === "SENT"
                                  ? "not-allowed"
                                  : "pointer",
                              backgroundColor:
                                order?.reportStatus === "SENT" ? "#f5f5f5" : "",
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                      {(order?.testResults || []).length === 0 ? (
                        !order?.resultLink && (
                          <p style={{ color: "#888", padding: "8px 0" }}>
                            No test results uploaded yet.
                          </p>
                        )
                      ) : (
                        order.testResults.map((doc, i) => (
                          <div
                            key={doc.id || i}
                            className={styles["result-file-item"]}
                          >
                            <span className={styles["result-file-icon"]}>
                              <AiOutlineFilePdf size={20} color="#E55353" />
                            </span>
                            <span
                              className={styles["result-file-name"]}
                              onClick={() =>
                                handleFileClick(doc.id, doc.fileName)
                              }
                              style={{
                                cursor: "pointer",
                                color: "#155DFC",
                                textDecoration: "underline",
                              }}
                            >
                              {doc.fileName || `Test Result ${i + 1}.pdf`}
                            </span>
                            <button
                              className={styles["result-delete-btn"]}
                              onClick={() =>
                                handleFileDelete(doc.id, doc.fileName)
                              }
                              disabled={order?.reportStatus === "SENT"}
                              style={{
                                opacity:
                                  order?.reportStatus === "SENT" ? 0.4 : 1,
                                cursor:
                                  order?.reportStatus === "SENT"
                                    ? "not-allowed"
                                    : "pointer",
                                backgroundColor:
                                  order?.reportStatus === "SENT"
                                    ? "#f5f5f5"
                                    : "",
                              }}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    <div className={styles["result-actions"]}>
                      <button
                        className={styles["result-share-btn"]}
                        onClick={handleShareWithPatient}
                        disabled={order?.reportStatus === "SENT"}
                        style={{
                          opacity: order?.reportStatus === "SENT" ? 0.6 : 1,
                          cursor:
                            order?.reportStatus === "SENT"
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            order?.reportStatus === "SENT" ? "#ccc" : "",
                        }}
                      >
                        <Check size={16} />{" "}
                        {order?.reportStatus === "SENT"
                          ? "Already Shared"
                          : "Share with Patient & Doctor"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Shared invoice modal — identical layout + fee math everywhere. */}
      {showInvoiceModal && (
        <ImagingInvoiceModal
          open={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          order={order}
          centerProfile={imagingCenterProfile}
          adminFeePercentage={adminFeePercentage}
        />
      )}

      {/* Add / Replace Result Link Modal — styled like the Add New Test dialog */}
      {showResultLinkModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={closeResultLinkModal}
                disabled={savingResultLink}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>
                {order?.resultLink ? "Replace Result Link" : "Add Result Link"}
              </h2>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Paste a link to the test results, for example an online image
                viewer. Like an uploaded report, it will be available in the
                patient's app.
              </p>
              <label
                htmlFor="result-link-input"
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  fontWeight: 500,
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Result link
              </label>
              <input
                id="result-link-input"
                type="url"
                inputMode="url"
                value={resultLinkInput}
                onChange={(e) => {
                  setResultLinkInput(e.target.value);
                  if (resultLinkError) setResultLinkError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveResultLink();
                  }
                }}
                placeholder="https://"
                maxLength={RESULT_LINK_MAX_LENGTH}
                autoFocus
                disabled={savingResultLink}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${resultLinkError ? "#dc2626" : "#d1d5db"}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  marginBottom: "4px",
                  boxSizing: "border-box",
                }}
              />
              {resultLinkError && (
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "13px",
                    color: "#dc2626",
                  }}
                >
                  {resultLinkError}
                </p>
              )}
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={closeResultLinkModal}
                  disabled={savingResultLink}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  Cancel
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={handleSaveResultLink}
                  disabled={savingResultLink}
                  style={{
                    opacity: savingResultLink ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Link2 size={16} />{" "}
                  {savingResultLink ? "Saving..." : "Save Link"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accept Order Confirmation Modal — styled like the Add New Test dialog */}
      {showAcceptModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={() => setShowAcceptModal(false)}
                disabled={acceptingOrder}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>Accept Order</h2>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Accept this order? The patient will be notified and you can then
                add tests and send the invoice.
              </p>
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={() => setShowAcceptModal(false)}
                  disabled={acceptingOrder}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  Cancel
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmAcceptOrder}
                  disabled={acceptingOrder}
                  style={{
                    opacity: acceptingOrder ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Check size={16} />{" "}
                  {acceptingOrder ? "Accepting..." : "Accept"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Order Confirmation Modal — styled like the Add New Test dialog */}
      {showRejectModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={() => {
                  setShowRejectModal(false);
                  resetRejectForm();
                }}
                disabled={rejectingOrder}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>Reject Order</h2>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Reject this order? The patient will be notified. This will move
                the order to the Rejected tab.
              </p>
              <label
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  fontWeight: 500,
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Reason (optional)
              </label>
              {/* Admin-configured reasons for this center + an "Other" escape
                  hatch that reveals the free-text box below. The reason text
                  itself follows the app locale (Arabic when the UI is Arabic). */}
              <select
                value={selectedRejectReasonId}
                onChange={(e) => {
                  setSelectedRejectReasonId(e.target.value);
                  // Drop any half-typed custom text when switching to a preset.
                  if (e.target.value !== CUSTOM_REJECTION_REASON)
                    setRejectReason("");
                }}
                disabled={rejectingOrder || loadingRejectReasons}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  marginBottom: isCustomRejectReason ? "10px" : "4px",
                  boxSizing: "border-box",
                  backgroundColor: "#fff",
                  cursor: rejectingOrder ? "not-allowed" : "pointer",
                }}
              >
                <option value="">
                  {loadingRejectReasons
                    ? "Loading..."
                    : "Select a reason (optional)"}
                </option>
                {rejectReasonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value={CUSTOM_REJECTION_REASON}>
                  Other (type your own reason)
                </option>
              </select>
              {isCustomRejectReason && (
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Add a reason for rejecting this order..."
                  rows={3}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    resize: "vertical",
                    marginBottom: "4px",
                    boxSizing: "border-box",
                  }}
                />
              )}
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={() => {
                    setShowRejectModal(false);
                    resetRejectForm();
                  }}
                  disabled={rejectingOrder}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  Cancel
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmRejectOrder}
                  disabled={rejectingOrder}
                  style={{
                    opacity: rejectingOrder ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  {rejectingOrder ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LabDashboardLayout>
  );
};

export default ImagingAppointmentDetail;
